import React, { useState, useRef, useEffect, useCallback } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const GOOGLE_FONTS_URL = 'https://fonts.googleapis.com/css2?family=Alex+Brush&family=Bangers&family=Black+Ops+One&family=Graduate&family=Permanent+Marker&family=Stardos+Stencil:wght@700&family=Oswald:wght@400;700&family=Pacifico&family=Righteous&family=Bebas+Neue&family=Dancing+Script:wght@700&family=Lobster&family=Press+Start+2P&display=swap';

const PRODUCTS = {
  tshirt:     { name: 'Short-Sleeve T-Shirt', basePrice: 22.00, xl2Price: 26.00, xl3Price: 29.00 },
  polo:       { name: 'Polo Shirt',            basePrice: 32.00, xl2Price: 36.00, xl3Price: 39.00 },
  longsleeve: { name: 'Long Sleeve',           basePrice: 26.00, xl2Price: 30.00, xl3Price: 33.00 },
  hoodie:     { name: 'Pullover Hoodie',        basePrice: 36.00, xl2Price: 42.00, xl3Price: 46.00 },
  sweatshirt: { name: 'Crewneck Sweatshirt',    basePrice: 32.00, xl2Price: 37.00, xl3Price: 40.00 }
};

const ADULT_SIZES = ['S','M','L','XL','2XL','3XL'];
const KIDS_SIZES  = ['2T','3T','4T','Youth XS','Youth S','Youth M','Youth L','Youth XL'];
const ALL_SIZES   = [...ADULT_SIZES, ...KIDS_SIZES];
// Kept for backward-compatibility with any older reference
const SIZES = ADULT_SIZES;

const FONTS = [
  { id: 'sans-serif',                  name: 'Standard Clean' },
  { id: 'Impact, sans-serif',          name: '⚡ Athletic Bold' },
  { id: '"Graduate", serif',           name: '🏫 College Block' },
  { id: '"Black Ops One", cursive',    name: '🪖 Heavy Stencil' },
  { id: '"Stardos Stencil", serif',    name: '🪖 Classic Stencil' },
  { id: '"Bangers", cursive',          name: '💥 Action Pop' },
  { id: '"Permanent Marker", cursive', name: '🎨 Graffiti Marker' },
  { id: '"Alex Brush", cursive',       name: '✍️ Classic Script' },
  { id: '"Oswald", sans-serif',        name: '🏋️ Modern Condensed' },
  { id: '"Pacifico", cursive',         name: '🌊 Retro Surf' },
  { id: '"Righteous", cursive',        name: '🎮 Funky Rounded' },
  { id: '"Bebas Neue", cursive',       name: '📺 Display Caps' },
  { id: '"Dancing Script", cursive',   name: '💃 Elegant Writing' },
  { id: '"Lobster", cursive',          name: '🦞 Vintage Script' },
  { id: '"Press Start 2P", cursive',   name: '👾 8-Bit Pixel' }
];

const FONT_COLORS = [
  { hex: '#ffffff', name: 'White' },        { hex: '#000000', name: 'Black' },
  { hex: '#1e3a8a', name: 'Navy Blue' },    { hex: '#b91c1c', name: 'Varsity Red' },
  { hex: '#14532d', name: 'Forest Green' }, { hex: '#78350f', name: 'Dark Brown' },
  { hex: '#ef4444', name: 'Bright Red' },   { hex: '#f97316', name: 'Orange' },
  { hex: '#eab308', name: 'Yellow' },       { hex: '#22c55e', name: 'Lime Green' },
  { hex: '#06b6d4', name: 'Cyan' },         { hex: '#3b82f6', name: 'Blue' },
  { hex: '#8b5cf6', name: 'Purple' },       { hex: '#ec4899', name: 'Hot Pink' },
  { hex: '#f43f5e', name: 'Rose' },         { hex: '#10b981', name: 'Emerald' },
  { hex: '#fbbf24', name: 'Gold' },         { hex: '#a78bfa', name: 'Lavender' },
  { hex: '#FFD700', name: '✨ Gold' },       { hex: '#C0C0C0', name: '✨ Silver' },
  { hex: '#B87333', name: '✨ Copper' },    { hex: '#E5E4E2', name: '✨ Platinum' },
  { hex: '#ff6b6b', name: 'Coral' },        { hex: '#4ecdc4', name: 'Turquoise' }
];

const WEIGHTS = [
  { value: '400', label: 'Regular' },
  { value: '700', label: 'Bold' },
  { value: '900', label: 'Ultra Bold' }
];

const SHIRT_SWATCHES = [
  { hex: '#ffffff', name: 'White' },  { hex: '#1b1b1b', name: 'Black' },
  { hex: '#94a3b8', name: 'Grey' },   { hex: '#d4c5a9', name: 'Sand' },
  { hex: '#6b7280', name: 'Ash' },    { hex: '#1e3a8a', name: 'Navy' },
  { hex: '#b91c1c', name: 'Red' },    { hex: '#14532d', name: 'Green' },
  { hex: '#7c3aed', name: 'Purple' }, { hex: '#0891b2', name: 'Sky Blue' },
  { hex: '#c2410c', name: 'Orange' }, { hex: '#be185d', name: 'Pink' },
  { hex: '#f9a8d4', name: 'Light Pink' }, { hex: '#854d0e', name: 'Mustard' }
];

const EMPTY_LOCATIONS = { front: [], back: [], left_sleeve: [], right_sleeve: [] };

// Location surcharges per shirt (market rate 2025)
const LOCATION_FEES = { front: 0, back: 5.00, left_sleeve: 3.00, right_sleeve: 3.00 };
const SALES_TAX_RATE = 0.06; // Virginia Beach combined sales tax
const COMPLEXITY_FEE = 3.00; // added per location when design includes any image

// Print methods — DTF and Vinyl only (screen print discontinued)
const PRINT_METHODS = [
  { val:'dtf',   icon:'🎨', label:'DTF Direct-to-Film', desc:'Full color, photos & gradients · Any qty', surcharge:2.50, note:'+$2.50 per shirt' },
  { val:'vinyl', icon:'✂️', label:'Vinyl / HTV',         desc:'Names, numbers & specialty finishes',      surcharge:4.00, note:'+$4.00 per shirt' }
];

// ── Build a thick 8-direction text outline that works on all fonts ────────────
function buildOutline(color = '#000000', thickness = 2) {
  const d = thickness;
  const dirs = [
    `${d}px ${d}px 0 ${color}`,   `-${d}px -${d}px 0 ${color}`,
    `${d}px -${d}px 0 ${color}`,  `-${d}px ${d}px 0 ${color}`,
    `${d}px 0 0 ${color}`,         `-${d}px 0 0 ${color}`,
    `0 ${d}px 0 ${color}`,         `0 -${d}px 0 ${color}`,
    `${d+1}px 0 0 ${color}`,       `-${d+1}px 0 0 ${color}`,
    `0 ${d+1}px 0 ${color}`,       `0 -${d+1}px 0 ${color}`,
  ];
  return dirs.join(', ');
}

// ── Extract a safe point from mouse OR touch events ───────────────────────────
function getEventPoint(e) {
  if (e.touches && e.touches.length > 0) {
    return { x: e.touches[0].clientX, y: e.touches[0].clientY };
  }
  if (e.changedTouches && e.changedTouches.length > 0) {
    return { x: e.changedTouches[0].clientX, y: e.changedTouches[0].clientY };
  }
  return { x: e.clientX, y: e.clientY };
}

// ─── useIsMobile ──────────────────────────────────────────────────────────────
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return isMobile;
}

// ─── Server helpers ───────────────────────────────────────────────────────────
async function postJSON(path, body) {
  const res = await fetch(`/.netlify/functions/${path}`, { method:'POST', headers:{ 'Content-Type':'application/json' }, body: JSON.stringify(body) });
  let data = {};
  try { data = await res.json(); } catch (e) {}
  return { ok: res.ok, status: res.status, data };
}

const LOC_LABELS = { front:'Front', back:'Back', left_sleeve:'Left sleeve', right_sleeve:'Right sleeve' };

// Short text description of the design (saved with the order inside Stripe)
function summarizeDesign(locationElements) {
  const out = [];
  Object.keys(LOC_LABELS).forEach(loc => (locationElements[loc] || []).forEach(el => {
    if (out.length >= 12) return;
    if (el.type === 'text') out.push({ loc: LOC_LABELS[loc], kind:'text', text:String(el.content || '').slice(0, 60), font:String(el.fontFamily || '').split(',')[0].replace(/['"]/g,'').slice(0, 24), color:el.color });
    else out.push({ loc: LOC_LABELS[loc], kind:'image' });
  }));
  return out;
}

const loadImg = src => new Promise(res => { const i = new Image(); i.onload = () => res(i); i.onerror = () => res(null); i.src = src; });

// Draws each location's design onto a canvas (same layout as the on-screen print area)
// so the owner receives real preview images plus the original uploaded files.
async function buildDesigns(locationElements, shirtColor) {
  const previews = {}; const originals = [];
  const SCALE = 2;
  for (const loc of Object.keys(LOC_LABELS)) {
    const els = locationElements[loc] || [];
    if (!els.length) continue;
    const sleeve = loc.includes('sleeve');
    const W = sleeve ? 130 : 220, H = sleeve ? 340 : 280;
    try {
      const c = document.createElement('canvas'); c.width = W * SCALE; c.height = H * SCALE;
      const ctx = c.getContext('2d');
      ctx.fillStyle = shirtColor || '#ffffff'; ctx.fillRect(0, 0, c.width, c.height);
      ctx.scale(SCALE, SCALE);
      for (const el of els) {
        ctx.save();
        const px = (el.x / 100) * W, py = (el.y / 100) * H;
        if (el.type === 'image') {
          const img = await loadImg(el.src); if (!img) { ctx.restore(); continue; }
          const size = el.width || 80, cx = px + 2 + size / 2, cy = py + 2 + size / 2;
          ctx.translate(cx, cy);
          if (el.rotation) ctx.rotate(el.rotation * Math.PI / 180);
          ctx.scale(el.flipH ? -1 : 1, el.flipV ? -1 : 1);
          ctx.globalAlpha = el.opacity ?? 1;
          const r = Math.min(size / img.width, size / img.height);
          ctx.drawImage(img, -img.width * r / 2, -img.height * r / 2, img.width * r, img.height * r);
          originals.push(el.src);
        } else {
          const fs = el.fontSize || 26;
          ctx.font = `${el.fontWeight || '700'} ${fs}px ${el.fontFamily || 'sans-serif'}`;
          ctx.textBaseline = 'middle';
          const w = ctx.measureText(el.content || '').width;
          const cx = px + 4 + w / 2, cy = py + 4 + fs * 0.6;
          ctx.translate(cx, cy);
          if (el.rotation) ctx.rotate(el.rotation * Math.PI / 180);
          ctx.scale(el.flipH ? -1 : 1, el.flipV ? -1 : 1);
          if (el.hasOutline) { ctx.lineJoin = 'round'; ctx.lineWidth = (el.outlineThickness || 2) * 2; ctx.strokeStyle = el.outlineColor || '#000000'; ctx.strokeText(el.content || '', -w / 2, 0); }
          if (el.hasShadow) { ctx.shadowColor = el.shadowColor || '#000000'; ctx.shadowOffsetX = 3; ctx.shadowOffsetY = 3; ctx.shadowBlur = 6; }
          ctx.fillStyle = el.color || '#000000';
          ctx.fillText(el.content || '', -w / 2, 0);
        }
        ctx.restore();
      }
      previews[loc] = c.toDataURL('image/jpeg', 0.85);
    } catch (e) { console.error('preview failed', loc, e); }
  }
  // keep the request comfortably under the server's size limit
  let total = Object.values(previews).reduce((n, u) => n + u.length, 0);
  const keep = [];
  for (const o of originals) { if (total + o.length < 3500000) { keep.push(o); total += o.length; } }
  return { previews, originals: keep };
}

// Admin calls are verified on the server with the admin password
async function adminCall(path, password, body) {
  return postJSON(path, { password, adminPassword: password, ...body });
}

function CheckoutForm({ grandTotal, getDesigns, onSuccess }) {
  const stripe   = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error,      setError]      = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setProcessing(true); setError('');
    const { error: submitErr } = await elements.submit();
    if (submitErr) { setError(submitErr.message); setProcessing(false); return; }
    const { error: confirmErr, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: window.location.href },
      redirect: 'if_required'
    });
    if (confirmErr) { setError(confirmErr.message); setProcessing(false); return; }
    // Payment is done. Save the order + send emails (server verifies with Stripe).
    // Retries a few times; Stripe's webhook is the backup if this still fails.
    if (paymentIntent && paymentIntent.id) {
      let designs = null;
      try { designs = await getDesigns(); } catch (err) { console.error(err); }
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          const r = await postJSON('confirm-order', { paymentIntentId: paymentIntent.id, designs });
          if (r.ok) break;
        } catch (err) { console.error('confirm-order attempt failed', err); }
        await new Promise(res => setTimeout(res, 1500));
      }
    }
    onSuccess();
  };

  return (
    <form onSubmit={handleSubmit} style={{ display:'flex', flexDirection:'column', gap:'16px' }}>
      <PaymentElement options={{ layout:'tabs' }}/>
      {error && <div style={{ background:'#450a0a', border:'1px solid #991b1b', color:'#fca5a5', padding:'10px 14px', borderRadius:'8px', fontSize:'13px' }}>⚠️ {error}</div>}
      <button type="submit" disabled={!stripe||processing} style={{ width:'100%', padding:'16px', backgroundColor:(!stripe||processing)?'#475569':'#635BFF', color:'#fff', border:'none', borderRadius:'12px', fontWeight:'800', fontSize:'16px', cursor:(!stripe||processing)?'not-allowed':'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:'8px' }}>
        {processing ? <><span style={{ width:'16px',height:'16px',border:'2px solid rgba(255,255,255,0.3)',borderTopColor:'#fff',borderRadius:'50%',display:'inline-block',animation:'spin .8s linear infinite' }}/> Processing...</> : `⚡ Pay $${grandTotal.toFixed(2)} Securely`}
      </button>
      <p style={{ margin:0, fontSize:'11px', color:'#64748b', textAlign:'center' }}>🔒 256-bit SSL · Powered by Stripe</p>
    </form>
  );
}

// ─── Stripe Modal ─────────────────────────────────────────────────────────────
function StripeModal({ grandTotal, orderData, getDesigns, onClose, onSuccess }) {
  const [clientSecret, setClientSecret] = useState('');
  const [loading,      setLoading]      = useState(true);
  const [fetchError,   setFetchError]   = useState('');
  const orderRef = useRef(orderData);
  orderRef.current = orderData;

  useEffect(() => {
    fetch('/.netlify/functions/create-payment-intent', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ amount: Math.round(grandTotal * 100), customerEmail: orderRef.current.customerEmail, order: orderRef.current })
    })
    .then(r => r.json())
    .then(d => { if (d.clientSecret) { setClientSecret(d.clientSecret); setLoading(false); } else { setFetchError('Could not initialize payment. Please try again.'); setLoading(false); } })
    .catch(() => { setFetchError('Network error. Please check your connection.'); setLoading(false); });
  }, [grandTotal, orderData.customerEmail, orderData.product, orderData.totalQuantity]);

  const appearance = { theme:'night', variables:{ colorPrimary:'#6366f1', colorBackground:'#0f172a', colorText:'#f8fafc', borderRadius:'8px' } };

  return (
    <div style={{ position:'fixed', inset:0, backgroundColor:'rgba(0,0,0,0.85)', display:'flex', alignItems:'flex-end', justifyContent:'center', zIndex:9999 }}>
      <div style={{ backgroundColor:'#1e293b', borderRadius:'20px 20px 0 0', padding:'24px', width:'100%', maxWidth:'500px', border:'1px solid #334155', maxHeight:'90vh', overflowY:'auto', position:'relative' }}>
        <div style={{ width:'40px', height:'4px', backgroundColor:'#475569', borderRadius:'2px', margin:'0 auto 20px' }}/>
        <button onClick={onClose} style={{ position:'absolute', top:'16px', right:'16px', background:'none', border:'none', color:'#64748b', fontSize:'22px', cursor:'pointer', padding:'8px' }}>✕</button>
        <div style={{ display:'flex', alignItems:'center', gap:'12px', marginBottom:'20px' }}>
          <div style={{ backgroundColor:'#635BFF', borderRadius:'10px', padding:'8px 14px' }}><span style={{ color:'#fff', fontWeight:'800', fontSize:'15px' }}>⚡ stripe</span></div>
          <div><p style={{ margin:0, fontSize:'12px', color:'#94a3b8' }}>Secure Checkout</p><p style={{ margin:0, fontSize:'22px', fontWeight:'800', color:'#fff' }}>${grandTotal.toFixed(2)}</p></div>
        </div>
        {loading    && <div style={{ textAlign:'center', color:'#94a3b8', padding:'32px' }}>⏳ Initializing secure payment...</div>}
        {fetchError && <div style={{ background:'#450a0a', border:'1px solid #991b1b', color:'#fca5a5', padding:'14px', borderRadius:'8px', fontSize:'13px' }}>⚠️ {fetchError}</div>}
        {clientSecret && (
          <Elements stripe={stripePromise} options={{ clientSecret, appearance }}>
            <CheckoutForm grandTotal={grandTotal} getDesigns={getDesigns} onSuccess={onSuccess}/>
          </Elements>
        )}
      </div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

// ─── Payment Success Modal ────────────────────────────────────────────────────
function PaymentSuccessModal({ onClose }) {
  return (
    <div style={{ position:'fixed', inset:0, backgroundColor:'rgba(0,0,0,0.85)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:9999, padding:'20px' }}>
      <div style={{ backgroundColor:'#1e293b', borderRadius:'20px', padding:'40px 24px', width:'100%', maxWidth:'380px', border:'1px solid #166534', textAlign:'center' }}>
        <div style={{ fontSize:'64px', marginBottom:'16px' }}>✅</div>
        <h2 style={{ color:'#4ade80', fontSize:'22px', margin:'0 0 8px', fontWeight:'800' }}>Payment Successful!</h2>
        <p style={{ color:'#94a3b8', fontSize:'14px', margin:'0 0 24px', lineHeight:'1.6' }}>Your order is confirmed. Check your email for details.</p>
        <button onClick={onClose} style={{ padding:'14px 32px', backgroundColor:'#16a34a', color:'#fff', border:'none', borderRadius:'12px', fontWeight:'800', fontSize:'16px', cursor:'pointer', width:'100%' }}>🎉 Done</button>
      </div>
    </div>
  );
}

// ─── Admin Panel ──────────────────────────────────────────────────────────────
function AdminPanel() {
  const [trackingNumber, setTrackingNumber] = useState('');
  const [trackingUrl,    setTrackingUrl]    = useState('');
  const [estimatedDays,  setEstimatedDays]  = useState('3-5 business days');
  const [sending,        setSending]        = useState('');
  const [adminPwd,       setAdminPwd]       = useState('');
  const [authed,         setAuthed]         = useState(false);
  const [loginErr,       setLoginErr]       = useState('');
  const [orders,         setOrders]         = useState([]);
  const [loading,        setLoading]        = useState(false);
  const [loadErr,        setLoadErr]        = useState('');

  const loadOrders = async (pwd) => {
    setLoading(true); setLoadErr('');
    try {
      const r = await adminCall('admin-orders', pwd, { action:'list' });
      if (r.ok) setOrders(r.data.orders || []); else setLoadErr(r.data.error || 'Could not load orders');
    } catch (e) { setLoadErr('Network error loading orders'); }
    setLoading(false);
  };

  const login = async () => {
    setLoginErr('');
    try {
      const r = await adminCall('admin-orders', adminPwd, { action:'login' });
      if (r.ok) { setAuthed(true); loadOrders(adminPwd); }
      else setLoginErr(r.status === 401 ? 'Wrong password' : (r.data.error || 'Login failed'));
    } catch (e) { setLoginErr('Network error'); }
  };

  const inputStyle = { padding:'10px', borderRadius:'6px', border:'1px solid #334155', backgroundColor:'#1e293b', color:'#fff', fontSize:'13px' };

  if (!authed) return (
    <div style={{ backgroundColor:'#1e293b', padding:'24px', borderRadius:'16px', border:'1px solid #334155', maxWidth:'400px', margin:'0 auto' }}>
      <h3 style={{ color:'#818cf8', margin:'0 0 16px', fontSize:'16px' }}>🔐 Admin Panel</h3>
      <input type="password" value={adminPwd} onChange={e=>setAdminPwd(e.target.value)} onKeyDown={e=>e.key==='Enter'&&login()} placeholder="Admin password" style={{ ...inputStyle, width:'100%', boxSizing:'border-box', marginBottom:'10px' }}/>
      {loginErr && <p style={{ color:'#fca5a5', fontSize:'12px', margin:'0 0 10px' }}>⚠️ {loginErr}</p>}
      <button onClick={login} style={{ width:'100%', padding:'12px', backgroundColor:'#6366f1', color:'#fff', border:'none', borderRadius:'8px', fontWeight:'bold', cursor:'pointer' }}>Unlock</button>
    </div>
  );

  const sendUpdate = async (type, entry, status) => {
    const key = `${entry.id}-${type}`;
    setSending(key);
    const r = await adminCall('send-email', adminPwd, { type, orderData: { ...entry.order, trackingNumber, trackingUrl, estimatedDays } });
    if (r.ok) {
      await adminCall('admin-orders', adminPwd, { action:'status', paymentIntentId: entry.id, status });
      setOrders(list => list.map(o => o.id === entry.id ? { ...o, status } : o));
    } else alert('Failed to send: ' + (r.data.error || 'check your Resend settings in Netlify'));
    setSending('');
  };

  const STATUS = { new:['🆕 New','#1e3a8a','#93c5fd'], in_production:['🔄 In production','#78350f','#fcd34d'], ready:['🏪 Ready for pickup','#14532d','#4ade80'], shipped:['📦 Shipped','#14532d','#4ade80'] };

  return (
    <div style={{ backgroundColor:'#1e293b', padding:'24px', borderRadius:'16px', border:'2px solid #6366f1' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', margin:'0 0 16px' }}>
        <h3 style={{ color:'#818cf8', margin:0, fontSize:'16px', textTransform:'uppercase' }}>🎛️ Order Management</h3>
        <button onClick={()=>loadOrders(adminPwd)} disabled={loading} style={{ padding:'8px 14px', backgroundColor:'#334155', color:'#fff', border:'none', borderRadius:'8px', fontWeight:'bold', fontSize:'12px', cursor:'pointer' }}>{loading ? '⏳...' : '🔄 Refresh'}</button>
      </div>
      {loadErr && <p style={{ color:'#fca5a5', fontSize:'13px' }}>⚠️ {loadErr}</p>}
      {!loading && !loadErr && orders.length === 0 && <p style={{ color:'#475569', fontSize:'13px', fontStyle:'italic' }}>No paid orders found yet.</p>}
      {orders.length > 0 && (
        <div style={{ display:'flex', flexDirection:'column', gap:'8px', marginBottom:'16px' }}>
          <input value={estimatedDays} onChange={e=>setEstimatedDays(e.target.value)} placeholder="Estimated days (e.g. 3-5 business days)" style={inputStyle}/>
          <input value={trackingNumber} onChange={e=>setTrackingNumber(e.target.value)} placeholder="Tracking number (optional, used for the next 'Shipped' email)" style={inputStyle}/>
          <input value={trackingUrl} onChange={e=>setTrackingUrl(e.target.value)} placeholder="Tracking URL (optional)" style={inputStyle}/>
        </div>
      )}
      {orders.map(entry => {
        const o = entry.order || {};
        const st = STATUS[entry.status] || STATUS.new;
        const prodKey = `${entry.id}-production_update`, shipKey = `${entry.id}-shipping_update`;
        const sizeList = Object.entries(o.sizes || {}).filter(([,q])=>q>0).map(([k,q])=>`${k}×${q}`).join('  ');
        return (
          <div key={entry.id} style={{ backgroundColor:'#0f172a', padding:'16px', borderRadius:'10px', border:'1px solid #334155', marginBottom:'12px' }}>
            <div style={{ display:'flex', justifyContent:'space-between', flexWrap:'wrap', gap:'8px', marginBottom:'10px' }}>
              <div>
                <p style={{ margin:0, color:'#fff', fontWeight:'bold', fontSize:'14px' }}>{o.customerEmail}</p>
                <p style={{ margin:'2px 0 0', color:'#94a3b8', fontSize:'12px' }}>{o.customerPhone || 'No phone'} · {new Date(entry.created).toLocaleString()}</p>
                <p style={{ margin:'2px 0 0', color:'#94a3b8', fontSize:'12px' }}>{o.totalQuantity} pcs · ${Number(entry.paid).toFixed(2)} · {o.product}{o.shirtColorName ? ` · ${o.shirtColorName}` : ''} · {String(o.printMethod || '').toUpperCase()} · {o.shippingMethod === 'pickup' ? 'Pickup' : 'Shipping'}</p>
              </div>
              <span style={{ alignSelf:'flex-start', backgroundColor:st[1], color:st[2], padding:'3px 10px', borderRadius:'20px', fontSize:'11px', fontWeight:'bold' }}>{st[0]}</span>
            </div>
            {sizeList && <p style={{ margin:'0 0 8px', color:'#cbd5e1', fontSize:'12px' }}>📏 {sizeList}</p>}
            {(o.designSummary || []).length > 0 && (
              <div style={{ margin:'0 0 8px', color:'#cbd5e1', fontSize:'12px' }}>
                🎨 {(o.designSummary).map((d,i)=><div key={i} style={{ marginLeft:'18px' }}>{d.loc}: {d.kind === 'text' ? `"${d.text}" · ${d.font} · ${d.color}` : 'uploaded image'}</div>)}
              </div>
            )}
            {o.designNotes && <p style={{ margin:'0 0 8px', color:'#93c5fd', fontSize:'12px' }}>📝 {o.designNotes}</p>}
            {(entry.design !== 'received' || !entry.emails.owner || !entry.emails.customer) && (
              <p style={{ margin:'0 0 8px', color:'#fbbf24', fontSize:'12px', fontWeight:'bold' }}>
                ⚠️ {entry.design !== 'received' ? 'Design file was not received. Contact the customer. ' : ''}
                {!entry.emails.owner ? 'Owner email not sent. ' : ''}{!entry.emails.customer ? 'Customer confirmation not sent. ' : ''}
                {entry.emails.error ? `(${entry.emails.error})` : ''}
              </p>
            )}
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px' }}>
              <button onClick={()=>sendUpdate('production_update', entry, 'in_production')} disabled={!!sending} style={{ padding:'12px', backgroundColor:sending===prodKey?'#475569':'#1e3a8a', color:'#93c5fd', border:'none', borderRadius:'8px', fontWeight:'bold', fontSize:'13px', cursor:'pointer' }}>
                {sending===prodKey?'⏳...':'🔄 In Production'}
              </button>
              <button onClick={()=>sendUpdate('shipping_update', entry, o.shippingMethod==='pickup'?'ready':'shipped')} disabled={!!sending} style={{ padding:'12px', backgroundColor:sending===shipKey?'#475569':'#312e81', color:'#c4b5fd', border:'none', borderRadius:'8px', fontWeight:'bold', fontSize:'13px', cursor:'pointer' }}>
                {sending===shipKey?'⏳...':o.shippingMethod==='pickup'?'🏪 Ready Pickup':'📦 Shipped'}
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Collapsible Section ──────────────────────────────────────────────────────
function Section({ title, children, defaultOpen=true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ backgroundColor:'#0f172a', borderRadius:'10px', border:'1px solid #334155', overflow:'hidden' }}>
      <button onClick={()=>setOpen(o=>!o)} style={{ width:'100%', display:'flex', alignItems:'center', justifyContent:'space-between', padding:'11px 12px', background:'none', border:'none', cursor:'pointer', color:'#818cf8', fontSize:'11px', fontWeight:'800', textTransform:'uppercase', letterSpacing:'0.5px' }}>
        <span>{title}</span>
        <span style={{ fontSize:'12px', color:'#475569', transform:open?'rotate(180deg)':'none', display:'inline-block', transition:'transform 0.2s' }}>▼</span>
      </button>
      {open && <div style={{ padding:'10px 12px 14px' }}>{children}</div>}
    </div>
  );
}

// ─── MAIN APP ─────────────────────────────────────────────────────────────────
export default function App() {
  const isMobile = useIsMobile();

  // ── State ─────────────────────────────────────────────────────────────────
  const [activeProduct,       setActiveProduct]       = useState('tshirt');
  const [activeLocation,      setActiveLocation]      = useState('front');
  const [shirtColor,          setShirtColor]          = useState('#ffffff');
  const [locationElements,    setLocationElements]    = useState(EMPTY_LOCATIONS);
  const [selectedElementId,   setSelectedElementId]   = useState(null);
  const [textInput,           setTextInput]           = useState('');
  const [sizes,               setSizes]               = useState(ALL_SIZES.reduce((o,s)=>({ ...o, [s]:0 }), {}));
  const [ageGroup,            setAgeGroup]            = useState('adult'); // 'adult' | 'kids' (kids only for t-shirts)
  const [shippingMethod,      setShippingMethod]      = useState('standard');
  const [customerEmail,       setCustomerEmail]       = useState('');
  const [customerPhone,       setCustomerPhone]       = useState('');
  const [isSubmitting,        setIsSubmitting]        = useState(false);
  const [layoutChecked,       setLayoutChecked]       = useState(false);
  const [showStripe,          setShowStripe]          = useState(false);
  const [paymentSuccess,      setPaymentSuccess]      = useState(false);
  const [editingElementId,    setEditingElementId]    = useState(null);
  const [editingText,         setEditingText]         = useState('');

  const [showAdmin,           setShowAdmin]           = useState(false);
  const [printMethod,         setPrintMethod]         = useState('dtf');
  const [designNotes,         setDesignNotes]         = useState('');
  const [zoomLevel,           setZoomLevel]           = useState(1);

  // ── Interaction state kept in refs so pointer handlers never go stale ─────
  const isDraggingRef    = useRef(false);
  const isResizingRef    = useRef(false);
  const resizeHandleRef  = useRef('');
  const interactionRef   = useRef({ mouseX:0, mouseY:0, elX:0, elY:0, elSize:0 });
  const selectedIdRef    = useRef(null);
  // mirror state → ref so callbacks always see latest
  useEffect(()=>{ selectedIdRef.current = selectedElementId; }, [selectedElementId]);

  // ── History ───────────────────────────────────────────────────────────────
  const historyRef      = useRef([EMPTY_LOCATIONS]);
  const historyIndexRef = useRef(0);
  const [, forceRender] = useState(0);

  const pushHistory = useCallback(snap => {
    const h = historyRef.current.slice(0, historyIndexRef.current + 1);
    h.push(snap);
    historyRef.current = h;
    historyIndexRef.current = h.length - 1;
    forceRender(n => n + 1);
  }, []);

  const canUndo = historyIndexRef.current > 0;
  const canRedo = historyIndexRef.current < historyRef.current.length - 1;

  const undo = useCallback(() => {
    if (historyIndexRef.current <= 0) return;
    historyIndexRef.current--;
    setLocationElements(historyRef.current[historyIndexRef.current]);
    setSelectedElementId(null); setEditingElementId(null); forceRender(n => n + 1);
  }, []);

  const redo = useCallback(() => {
    if (historyIndexRef.current >= historyRef.current.length - 1) return;
    historyIndexRef.current++;
    setLocationElements(historyRef.current[historyIndexRef.current]);
    setSelectedElementId(null); setEditingElementId(null); forceRender(n => n + 1);
  }, []);

  useEffect(() => {
    const onKey = e => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) { e.preventDefault(); undo(); }
      if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) { e.preventDefault(); redo(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [undo, redo]);

  const updateElements = useCallback(m => { setLocationElements(m); pushHistory(m); }, [pushHistory]);

  // ── Refs ──────────────────────────────────────────────────────────────────
  const canvasRef     = useRef(null);
  const imageInputRef = useRef(null);
  const locElemsRef   = useRef(locationElements);
  useEffect(() => { locElemsRef.current = locationElements; }, [locationElements]);
  const activeLocRef  = useRef(activeLocation);
  useEffect(() => { activeLocRef.current = activeLocation; }, [activeLocation]);
  const editingIdRef  = useRef(editingElementId);
  const editingTxtRef = useRef(editingText);
  useEffect(() => { editingIdRef.current = editingElementId; }, [editingElementId]);
  useEffect(() => { editingTxtRef.current = editingText; }, [editingText]);

  const currentElements = locationElements[activeLocation];
  const selectedElement = currentElements.find(el => el.id === selectedElementId) || null;
  const isSleeveActive  = activeLocation === 'left_sleeve' || activeLocation === 'right_sleeve';

  useEffect(() => {
    const link = document.createElement('link');
    link.rel = 'stylesheet'; link.href = GOOGLE_FONTS_URL;
    document.head.appendChild(link);
    return () => { if (document.head.contains(link)) document.head.removeChild(link); };
  }, []);

  // ── Pricing ───────────────────────────────────────────────────────────────
  const handleSizeChange = (size, val) => setSizes(prev => ({ ...prev, [size]: Math.max(0, parseInt(val) || 0) }));

  // Kids sizes are only offered on t-shirts; revert to adult for other products
  useEffect(() => {
    if (activeProduct !== 'tshirt' && ageGroup !== 'adult') setAgeGroup('adult');
  }, [activeProduct, ageGroup]);

  // Sizes shown in the grid depend on the selected age group
  const activeSizeList = ageGroup === 'kids' ? KIDS_SIZES : ADULT_SIZES;
  const kidsCount  = KIDS_SIZES.reduce((a,s)=>a+(sizes[s]||0),0);
  const adultCount = ADULT_SIZES.reduce((a,s)=>a+(sizes[s]||0),0);
  const totalQuantity  = Object.values(sizes).reduce((a, b) => a + b, 0);
  const discountFactor = totalQuantity>=48?0.60:totalQuantity>=24?0.70:totalQuantity>=12?0.80:totalQuantity>=6?0.90:1.0;
  const discountPct    = totalQuantity>=48?40:totalQuantity>=24?30:totalQuantity>=12?20:totalQuantity>=6?10:0;
  const getBulkMsg = () => {
    if (!totalQuantity) return "Add items to unlock bulk discounts!";
    if (totalQuantity < 6)  return `💡 Add ${6-totalQuantity} more for 10% OFF!`;
    if (totalQuantity < 12) return `🏷️ 10% OFF Active! Add ${12-totalQuantity} more for 20% OFF!`;
    if (totalQuantity < 24) return `✅ 20% OFF Active! Add ${24-totalQuantity} more for 30% OFF!`;
    if (totalQuantity < 48) return `🎉 30% OFF Active! Add ${48-totalQuantity} more for 40% OFF!`;
    return "👑 Max Wholesale 40% OFF Unlocked!";
  };
  const printSurcharge = PRINT_METHODS.find(m => m.val === printMethod)?.surcharge || 0;

  // Location + complexity fees — per location that has design, per shirt
  const getLocationFee = (loc) => {
    const es = locationElements[loc];
    if (!es || !es.length) return 0;
    const hasImg = es.some(e => e.type === 'image');
    return LOCATION_FEES[loc] + (hasImg ? COMPLEXITY_FEE : 0);
  };
  const locationFeePerShirt = Object.keys(LOCATION_FEES).reduce((acc, l) => acc + getLocationFee(l), 0);

  const getUnitPrice = (size) => {
    const base = size === '3XL' ? PRODUCTS[activeProduct].xl3Price
               : size === '2XL' ? PRODUCTS[activeProduct].xl2Price
               : PRODUCTS[activeProduct].basePrice;
    return base + printSurcharge + locationFeePerShirt;
  };
  const orderTotal = Object.entries(sizes).reduce((acc, [size, qty]) => {
    if (!qty) return acc;
    return acc + qty * getUnitPrice(size) * discountFactor;
  }, 0);
  const shippingCost = !totalQuantity ? 0 : shippingMethod === 'pickup' ? 0 : orderTotal >= 150 ? 0 : 7.99;
  // Virginia Beach sales tax (4.3% state + 1% city + 0.7% regional = 6%), on the merchandise.
  // Everything is computed in whole cents so the amount charged always matches what is shown.
  const merchCents    = Math.round(orderTotal * 100);
  const taxCents      = Math.round(merchCents * SALES_TAX_RATE);
  const salesTax      = taxCents / 100;
  const grandTotal    = (merchCents + Math.round(shippingCost * 100) + taxCents) / 100;

  // ── Order submit ──────────────────────────────────────────────────────────
  const handleProcessOrderSubmit = e => {
    e.preventDefault();
    if (!totalQuantity)  { alert('Cart is empty!'); return; }
    if (!layoutChecked)  { alert('Please check the verification box.'); return; }
    if (!customerEmail)  { alert('Please enter your email.'); return; }
    setShowStripe(true);
  };

  const handlePaymentSuccess = () => {
    setShowStripe(false);
    setIsSubmitting(false);
    setPaymentSuccess(true);
  };

  // ── Image upload ──────────────────────────────────────────────────────────
  const handleImageUpload = e => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { alert('Please upload a valid image.'); return; }
    const reader = new FileReader();
    reader.onload = ev => {
      const img = { id: `img-${Date.now()}`, type: 'image', src: ev.target.result, x: 20, y: 20, width: 80, height: 80, opacity: 1, isLocked: false };
      const newMap = { ...locElemsRef.current, [activeLocRef.current]: [...locElemsRef.current[activeLocRef.current], img] };
      updateElements(newMap);
      setSelectedElementId(img.id);
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  // ── Pointer handlers (mouse + touch unified, NO stale closures) ───────────
  // We attach real event listeners to the canvas wrapper with { passive:false }
  // so we can call preventDefault() and stop the browser from scrolling.
  const printAreaRef = useRef(null); // the dashed print area div

  const onPointerDown = useCallback((e, el, mode, handleName = '') => {
    if (e.cancelable) e.preventDefault();
    e.stopPropagation();
    if (el.isLocked) return;
    const pt = getEventPoint(e);
    selectedIdRef.current = el.id;
    setSelectedElementId(el.id);
    interactionRef.current = { mouseX: pt.x, mouseY: pt.y, elX: el.x, elY: el.y, elSize: el.type === 'text' ? el.fontSize : el.width };
    if (mode === 'resize') { isResizingRef.current = true; resizeHandleRef.current = handleName; }
    else { isDraggingRef.current = true; }
  }, []);

  // Attach touchmove/touchend directly on the print area element so
  // passive:false works (React synthetic events are passive on mobile)
  useEffect(() => {
    const el = printAreaRef.current;
    if (!el) return;

    const onMove = e => {
      if (!isDraggingRef.current && !isResizingRef.current) return;
      if (e.cancelable) e.preventDefault();
      const selId = selectedIdRef.current;
      if (!selId || !canvasRef.current) return;
      const pt   = getEventPoint(e);
      const rect = canvasRef.current.getBoundingClientRect();
      const dX   = pt.x - interactionRef.current.mouseX;
      const dY   = pt.y - interactionRef.current.mouseY;
      const loc  = activeLocRef.current;

      setLocationElements(prev => {
        const updated = prev[loc].map(item => {
          if (item.id !== selId || item.isLocked) return item;
          if (isDraggingRef.current) {
            return {
              ...item,
              x: Math.max(0, Math.min(80, interactionRef.current.elX + (dX / rect.width) * 100)),
              y: Math.max(0, Math.min(85, interactionRef.current.elY + (dY / rect.height) * 100))
            };
          }
          if (isResizingRef.current) {
            const h = resizeHandleRef.current;
            if (item.type === 'text') {
              let sc = dX * 0.35;
              if (h === 'bl' || h === 'tl') sc = -sc;
              return { ...item, fontSize: Math.round(Math.max(12, Math.min(120, interactionRef.current.elSize + sc))) };
            } else {
              const sc = (h === 'bl' || h === 'tl') ? -dX : dX;
              const sz = Math.max(30, Math.min(220, interactionRef.current.elSize + sc));
              return { ...item, width: sz, height: sz };
            }
          }
          return item;
        });
        return { ...prev, [loc]: updated };
      });
    };

    const onEnd = () => {
      if (isDraggingRef.current || isResizingRef.current) {
        pushHistory({ ...locElemsRef.current });
      }
      isDraggingRef.current  = false;
      isResizingRef.current  = false;
    };

    el.addEventListener('touchmove', onMove, { passive: false });
    el.addEventListener('touchend',  onEnd,  { passive: false });
    return () => {
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('touchend',  onEnd);
    };
  }, [pushHistory]);

  // Mouse handlers (desktop) — still use React synthetic events
  const onMouseMove = useCallback(e => {
    if (!isDraggingRef.current && !isResizingRef.current) return;
    const selId = selectedIdRef.current;
    if (!selId || !canvasRef.current) return;
    const pt   = getEventPoint(e);
    const rect = canvasRef.current.getBoundingClientRect();
    const dX   = pt.x - interactionRef.current.mouseX;
    const dY   = pt.y - interactionRef.current.mouseY;
    const loc  = activeLocRef.current;
    setLocationElements(prev => {
      const updated = prev[loc].map(item => {
        if (item.id !== selId || item.isLocked) return item;
        if (isDraggingRef.current) return {
          ...item,
          x: Math.max(0, Math.min(80, interactionRef.current.elX + (dX / rect.width) * 100)),
          y: Math.max(0, Math.min(85, interactionRef.current.elY + (dY / rect.height) * 100))
        };
        if (isResizingRef.current) {
          const h = resizeHandleRef.current;
          if (item.type === 'text') {
            let sc = dX * 0.35;
            if (h === 'bl' || h === 'tl') sc = -sc;
            return { ...item, fontSize: Math.round(Math.max(12, Math.min(120, interactionRef.current.elSize + sc))) };
          } else {
            const sc = (h === 'bl' || h === 'tl') ? -dX : dX;
            const sz = Math.max(30, Math.min(220, interactionRef.current.elSize + sc));
            return { ...item, width: sz, height: sz };
          }
        }
        return item;
      });
      return { ...prev, [loc]: updated };
    });
  }, []);

  const onMouseUp = useCallback(() => {
    if (isDraggingRef.current || isResizingRef.current) pushHistory({ ...locElemsRef.current });
    isDraggingRef.current = false;
    isResizingRef.current = false;
  }, [pushHistory]);

  // ── Text editing ──────────────────────────────────────────────────────────
  const startEditingElement = el => {
    if (el.isLocked) return;
    setEditingElementId(el.id);
    setEditingText(el.content);
    setSelectedElementId(el.id);
  };

  const commitEdit = useCallback(() => {
    const id      = editingIdRef.current;
    const text    = editingTxtRef.current;
    const loc     = activeLocRef.current;
    if (!id) return;
    setEditingElementId(null); setEditingText('');
    const trimmed = text.trim();
    const elems   = locElemsRef.current[loc];
    if (!trimmed) {
      const nm = { ...locElemsRef.current, [loc]: elems.filter(el => el.id !== id) };
      setLocationElements(nm); pushHistory(nm); setSelectedElementId(null);
    } else {
      const nm = { ...locElemsRef.current, [loc]: elems.map(el => el.id === id ? { ...el, content: trimmed } : el) };
      setLocationElements(nm); pushHistory(nm);
    }
  }, [pushHistory]);

  // ── Element helpers ───────────────────────────────────────────────────────
  const addText = () => {
    if (!textInput.trim()) return;
    const el = { id: `text-${Date.now()}`, type:'text', content:textInput, x:25, y:35, color:'#000000', fontSize:26, fontFamily:'sans-serif', fontWeight:'700', letterSpacing:0, hasOutline:false, outlineColor:'#000000', rotation:0, isLocked:false };
    updateElements({ ...locationElements, [activeLocation]: [...currentElements, el] });
    setSelectedElementId(el.id); setTextInput('');

  };

  const handleDeleteElement = useCallback(idToDel => {
    const loc = activeLocRef.current;
    const nm  = { ...locElemsRef.current, [loc]: locElemsRef.current[loc].filter(el => el.id !== idToDel) };
    setLocationElements(nm); pushHistory(nm);
    setSelectedElementId(prev => prev === idToDel ? null : prev);
  }, [pushHistory]);

  const handlePropChange = (prop, val) =>
    setLocationElements(prev => ({ ...prev, [activeLocation]: prev[activeLocation].map(el => el.id === selectedElementId ? { ...el, [prop]: val } : el) }));

  const handlePropSave = (prop, val) =>
    updateElements({ ...locationElements, [activeLocation]: currentElements.map(el => el.id === selectedElementId ? { ...el, [prop]: val } : el) });

  const toggleLock = id => updateElements({ ...locationElements, [activeLocation]: currentElements.map(el => el.id === id ? { ...el, isLocked: !el.isLocked } : el) });

  const copyDesignToOtherSide = () => {
    const from = activeLocation;
    const to   = from === 'front' ? 'back' : from === 'back' ? 'front' : null;
    if (!to) { alert('Copy works between Front and Back only.'); return; }
    const copies = locationElements[from].map(el => ({ ...el, id: `${el.id}-copy-${Date.now()}` }));
    updateElements({ ...locationElements, [to]: copies });
    alert(`✅ Design copied to ${to}!`);
  };

  const duplicateElement = () => {
    if (!selectedElementId) return;
    const el = currentElements.find(e => e.id === selectedElementId);
    if (!el) return;
    const copy = { ...el, id: `${el.type}-${Date.now()}`, x: Math.min(el.x + 5, 75), y: Math.min(el.y + 5, 80) };
    updateElements({ ...locationElements, [activeLocation]: [...currentElements, copy] });
    setSelectedElementId(copy.id);
  };

  const flipHorizontal = () => {
    if (!selectedElementId) return;
    handlePropSave('flipH', !(selectedElement?.flipH || false));
  };

  const flipVertical = () => {
    if (!selectedElementId) return;
    handlePropSave('flipV', !(selectedElement?.flipV || false));
  };

  const bringForward = () => {
    if (!selectedElementId) return;
    const idx = currentElements.findIndex(el => el.id === selectedElementId);
    if (idx >= currentElements.length - 1) return;
    const arr = [...currentElements];
    [arr[idx], arr[idx + 1]] = [arr[idx + 1], arr[idx]];
    updateElements({ ...locationElements, [activeLocation]: arr });
  };

  const sendBackward = () => {
    if (!selectedElementId) return;
    const idx = currentElements.findIndex(el => el.id === selectedElementId);
    if (idx <= 0) return;
    const arr = [...currentElements];
    [arr[idx], arr[idx - 1]] = [arr[idx - 1], arr[idx]];
    updateElements({ ...locationElements, [activeLocation]: arr });
  };

  const centerEl = () => {
    if (!selectedElementId) return;
    updateElements({ ...locationElements, [activeLocation]: currentElements.map(el => el.id === selectedElementId ? { ...el, x: isSleeveActive ? 8 : 25 } : el) });
  };

  // Resize handle positions — bigger on mobile for easier finger targeting
  const HS = isMobile ? 22 : 14; // handle size px
  const HO = isMobile ? -11 : -7; // handle offset px
  const cornerHandles = {
    tl: { top:`${HO}px`, left:`${HO}px`,  cursor:'nwse-resize' },
    tr: { top:`${HO}px`, right:`${HO}px`, cursor:'nesw-resize' },
    bl: { bottom:`${HO}px`, left:`${HO}px`,  cursor:'nesw-resize' },
    br: { bottom:`${HO}px`, right:`${HO}px`, cursor:'nwse-resize' }
  };

  // Canvas dimensions
  const canvasWidth      = isMobile ? (isSleeveActive ? 150 : 280) : (isSleeveActive ? 190 : 400);
  const canvasHeight     = isMobile ? 340 : 420;
  const printAreaTop     = isSleeveActive ? (isMobile ? 28 : 35) : (isMobile ? 58 : 75);
  const printAreaWidth   = isSleeveActive ? (isMobile ? 100 : 130) : (isMobile ? 168 : 220);
  const printAreaHeight  = isSleeveActive ? (isMobile ? 280 : 340) : (isMobile ? 222 : 280);

  const orderData = { customerEmail, customerPhone, totalQuantity, discountPct, shippingMethod, grandTotal, subtotal: merchCents/100, shippingCost, salesTax, sizes: Object.fromEntries(Object.entries(sizes).filter(([,q])=>q>0)), product: PRODUCTS[activeProduct].name, shirtColorName: (SHIRT_SWATCHES.find(sw => sw.hex.toLowerCase() === String(shirtColor).toLowerCase()) || {}).name || shirtColor, printMethod, designNotes: String(designNotes||'').slice(0,800), designSummary: summarizeDesign(locationElements) };

  // ── Design Panel ──────────────────────────────────────────────────────────
  const DesignPanel = () => (
    <div style={{ display:'flex', flexDirection:'column', gap:'12px' }}>

      <Section title="🎯 Placement Zone">
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'6px' }}>
          {[{id:'front',label:'Front'},{id:'back',label:'Back'},{id:'left_sleeve',label:'Left Sleeve'},{id:'right_sleeve',label:'Right Sleeve'}].map(loc => (
            <button key={loc.id} onClick={() => { setActiveLocation(loc.id); setSelectedElementId(null); setEditingElementId(null); }}
              style={{ padding:'10px 6px', fontSize:'13px', fontWeight:'bold', backgroundColor:activeLocation===loc.id?'#6366f1':'#334155', color:'#fff', border:'none', borderRadius:'6px', cursor:'pointer' }}>
              {loc.label}
            </button>
          ))}
        </div>
      </Section>

      <div style={{ display:'flex', gap:'8px' }}>
        <button onClick={undo} disabled={!canUndo} style={{ flex:1, padding:'10px', backgroundColor:canUndo?'#1e3a8a':'#1e293b', color:canUndo?'#93c5fd':'#475569', border:`1px solid ${canUndo?'#3b82f6':'#334155'}`, borderRadius:'8px', fontWeight:'800', fontSize:'13px', cursor:canUndo?'pointer':'not-allowed' }}>↩ Undo</button>
        <button onClick={redo} disabled={!canRedo} style={{ flex:1, padding:'10px', backgroundColor:canRedo?'#1e3a8a':'#1e293b', color:canRedo?'#93c5fd':'#475569', border:`1px solid ${canRedo?'#3b82f6':'#334155'}`, borderRadius:'8px', fontWeight:'800', fontSize:'13px', cursor:canRedo?'pointer':'not-allowed' }}>Redo ↪</button>
      </div>

      <Section title="✏️ Add Text">
        <div style={{ display:'flex', gap:'6px' }}>
          <input type="text" value={textInput} onChange={e=>setTextInput(e.target.value)} onKeyDown={e=>e.key==='Enter'&&addText()} placeholder="Type wording here..." style={{ flexGrow:1, padding:'11px 12px', borderRadius:'6px', border:'1px solid #475569', backgroundColor:'#1e293b', color:'#fff', fontSize:'15px' }}/>
          <button onClick={addText} style={{ padding:'11px 16px', backgroundColor:'#6366f1', color:'white', border:'none', borderRadius:'6px', fontWeight:'bold', fontSize:'14px', cursor:'pointer' }}>Add</button>
        </div>
      </Section>

      <Section title="🖼️ Upload Image / Logo">
        <input ref={imageInputRef} type="file" accept="image/*" onChange={handleImageUpload} style={{ display:'none' }}/>
        <button onClick={() => imageInputRef.current.click()} style={{ width:'100%', padding:'13px', backgroundColor:'#312e81', color:'#c7d2fe', border:'1px solid #4338ca', borderRadius:'8px', fontWeight:'700', fontSize:'14px', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:'6px' }}>
          📁 Upload PNG / JPG / SVG
        </button>
        <p style={{ margin:'6px 0 0', fontSize:'11px', color:'#64748b', textAlign:'center' }}>Drag to move · Drag corner handles to resize</p>
      </Section>

      {/* Copy design to other side */}
      {(activeLocation === 'front' || activeLocation === 'back') && currentElements.length > 0 && (
        <button onClick={copyDesignToOtherSide} style={{ width:'100%', padding:'10px', backgroundColor:'#0f172a', color:'#818cf8', border:'1px solid #334155', borderRadius:'8px', fontSize:'12px', fontWeight:'bold', cursor:'pointer' }}>
          📋 Copy Design to {activeLocation === 'front' ? 'Back' : 'Front'}
        </button>
      )}

      {selectedElementId && (
        <div style={{ display:'flex', flexDirection:'column', gap:'6px' }}>
          <div style={{ display:'flex', gap:'6px' }}>
            <button onClick={centerEl} style={{ flexGrow:1, padding:'9px', backgroundColor:'#312e81', color:'#e0e7ff', border:'1px solid #4338ca', borderRadius:'6px', fontSize:'12px', fontWeight:'bold', cursor:'pointer' }}>🎯 Center</button>
            <button onClick={duplicateElement} title="Duplicate" style={{ padding:'9px 12px', backgroundColor:'#1e3a8a', color:'#93c5fd', border:'1px solid #3b82f6', borderRadius:'6px', fontSize:'12px', fontWeight:'bold', cursor:'pointer' }}>⧉ Dupe</button>
            <button onClick={() => toggleLock(selectedElementId)} style={{ padding:'9px 12px', backgroundColor:selectedElement?.isLocked?'#9a3412':'#475569', color:'#fff', border:'none', borderRadius:'6px', fontSize:'12px', fontWeight:'bold', cursor:'pointer' }}>
              {selectedElement?.isLocked ? '🔒' : '🔓'}
            </button>
            <button onClick={() => handleDeleteElement(selectedElementId)} style={{ padding:'9px 12px', backgroundColor:'#7f1d1d', color:'#fca5a5', border:'none', borderRadius:'6px', fontSize:'12px', fontWeight:'bold', cursor:'pointer' }}>🗑️</button>
          </div>
          <div style={{ display:'flex', gap:'6px' }}>
            <button onClick={flipHorizontal} title="Flip Horizontal" style={{ flex:1, padding:'8px 4px', backgroundColor:'#1e293b', color:'#94a3b8', border:'1px solid #334155', borderRadius:'6px', fontSize:'11px', fontWeight:'bold', cursor:'pointer' }}>↔ Flip H</button>
            <button onClick={flipVertical} title="Flip Vertical" style={{ flex:1, padding:'8px 4px', backgroundColor:'#1e293b', color:'#94a3b8', border:'1px solid #334155', borderRadius:'6px', fontSize:'11px', fontWeight:'bold', cursor:'pointer' }}>↕ Flip V</button>
            <button onClick={bringForward} title="Bring Forward" style={{ flex:1, padding:'8px 4px', backgroundColor:'#1e293b', color:'#94a3b8', border:'1px solid #334155', borderRadius:'6px', fontSize:'11px', fontWeight:'bold', cursor:'pointer' }}>⬆ Fwd</button>
            <button onClick={sendBackward} title="Send Backward" style={{ flex:1, padding:'8px 4px', backgroundColor:'#1e293b', color:'#94a3b8', border:'1px solid #334155', borderRadius:'6px', fontSize:'11px', fontWeight:'bold', cursor:'pointer' }}>⬇ Back</button>
          </div>
        </div>
      )}

      {/* TEXT CONTROLS */}
      {selectedElement && selectedElement.type === 'text' && (
        <Section title="🔤 Typography Controls">
          <div style={{ display:'flex', flexDirection:'column', gap:'12px' }}>

            {/* Edit text */}
            <div>
              <label style={{ fontSize:'11px', color:'#94a3b8', display:'block', marginBottom:'5px' }}>✏️ Edit Text</label>
              <div style={{ display:'flex', gap:'6px' }}>
                <input type="text"
                  value={editingElementId === selectedElement.id ? editingText : selectedElement.content}
                  onChange={e => { setEditingElementId(selectedElement.id); setEditingText(e.target.value); }}
                  onFocus={() => { setEditingElementId(selectedElement.id); setEditingText(selectedElement.content); }}
                  onBlur={commitEdit}
                  onKeyDown={e => { if (e.key==='Enter') { e.preventDefault(); commitEdit(); } if (e.key==='Escape') { setEditingElementId(null); setEditingText(''); } }}
                  style={{ flexGrow:1, padding:'10px', borderRadius:'6px', border:'1px solid #4338ca', backgroundColor:'#1e293b', color:'#fff', fontSize:'15px', outline:'none' }}
                />
                <button onMouseDown={e => { e.preventDefault(); commitEdit(); }} style={{ padding:'10px 14px', backgroundColor:'#4338ca', color:'white', border:'none', borderRadius:'6px', fontWeight:'bold', fontSize:'13px', cursor:'pointer' }}>Save</button>
              </div>
            </div>

            {/* Font family — onChange + onBlur both save so mobile works */}
            <div>
              <label style={{ fontSize:'11px', color:'#94a3b8', display:'block', marginBottom:'5px' }}>Font</label>
              <select
                value={selectedElement.fontFamily}
                onChange={e => handlePropSave('fontFamily', e.target.value)}
                style={{ width:'100%', padding:'10px', borderRadius:'6px', backgroundColor:'#1e293b', color:'#fff', border:'1px solid #475569', fontSize:'14px' }}
              >
                {FONTS.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
              </select>
            </div>

            {/* Weight */}
            <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'5px' }}>
              {WEIGHTS.map(w => (
                <button key={w.value} onClick={() => handlePropSave('fontWeight', w.value)} style={{ padding:'9px 4px', fontSize:'12px', fontWeight:w.value, backgroundColor:selectedElement.fontWeight===w.value?'#6366f1':'#334155', color:'#fff', border:'none', borderRadius:'6px', cursor:'pointer' }}>{w.label}</button>
              ))}
            </div>

            {/* Letter spacing */}
            <div>
              <label style={{ fontSize:'11px', color:'#94a3b8', display:'block', marginBottom:'4px' }}>Tracking (Letter Spacing): {selectedElement.letterSpacing || 0}px</label>
              <input type="range" min="-2" max="15"
                value={selectedElement.letterSpacing || 0}
                onChange={e => handlePropChange('letterSpacing', parseInt(e.target.value))}
                onMouseUp={e => handlePropSave('letterSpacing', parseInt(e.target.value))}
                onTouchEnd={e => handlePropSave('letterSpacing', parseInt(e.currentTarget.value))}
                style={{ width:'100%', accentColor:'#6366f1', height:'6px' }}
              />
            </div>

            {/* Rotation */}
            <div>
              <label style={{ fontSize:'11px', color:'#94a3b8', display:'block', marginBottom:'4px' }}>↻ Rotation: {selectedElement.rotation || 0}°</label>
              <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                <input type="range" min="-180" max="180"
                  value={selectedElement.rotation || 0}
                  onChange={e => handlePropChange('rotation', parseInt(e.target.value))}
                  onMouseUp={e => handlePropSave('rotation', parseInt(e.target.value))}
                  onTouchEnd={e => handlePropSave('rotation', parseInt(e.currentTarget.value))}
                  style={{ flexGrow:1, accentColor:'#6366f1', height:'6px' }}
                />
                <button onClick={() => handlePropSave('rotation', 0)} style={{ padding:'4px 8px', backgroundColor:'#334155', color:'#94a3b8', border:'none', borderRadius:'4px', fontSize:'11px', cursor:'pointer' }}>Reset</button>
              </div>
            </div>

            {/* Text shadow */}
            <div style={{ display:'flex', alignItems:'center', gap:'12px', flexWrap:'wrap', marginBottom:'4px' }}>
              <label style={{ display:'flex', alignItems:'center', gap:'8px', fontSize:'13px', color:'#94a3b8', cursor:'pointer' }}>
                <input type="checkbox"
                  checked={selectedElement.hasShadow || false}
                  onChange={e => handlePropSave('hasShadow', e.target.checked)}
                  style={{ accentColor:'#6366f1', width:'18px', height:'18px' }}
                />
                Drop Shadow
              </label>
              {selectedElement.hasShadow && (
                <div style={{ display:'flex', alignItems:'center', gap:'6px' }}>
                  <label style={{ fontSize:'11px', color:'#64748b' }}>Color:</label>
                  <input type="color" value={selectedElement.shadowColor || '#000000'}
                    onChange={e => handlePropChange('shadowColor', e.target.value)}
                    onBlur={e => handlePropSave('shadowColor', e.target.value)}
                    style={{ width:'36px', height:'24px', cursor:'pointer', border:'1px solid #475569', borderRadius:'4px', backgroundColor:'transparent' }}
                  />
                </div>
              )}
            </div>

            {/* Outline toggle + outline color */}
            <div style={{ display:'flex', alignItems:'center', gap:'12px', flexWrap:'wrap' }}>
              <label style={{ display:'flex', alignItems:'center', gap:'8px', fontSize:'13px', color:'#94a3b8', cursor:'pointer' }}>
                <input type="checkbox"
                  checked={selectedElement.hasOutline || false}
                  onChange={e => handlePropSave('hasOutline', e.target.checked)}
                  style={{ accentColor:'#6366f1', width:'18px', height:'18px' }}
                />
                Pop Outline
              </label>
              {selectedElement.hasOutline && (
                <div style={{ display:'flex', alignItems:'center', gap:'8px', flexWrap:'wrap' }}>
                  <label style={{ fontSize:'11px', color:'#64748b' }}>Color:</label>
                  <input type="color"
                    value={selectedElement.outlineColor || '#000000'}
                    onChange={e => handlePropChange('outlineColor', e.target.value)}
                    onBlur={e => handlePropSave('outlineColor', e.target.value)}
                    style={{ width:'36px', height:'24px', cursor:'pointer', border:'1px solid #475569', borderRadius:'4px', backgroundColor:'transparent' }}
                  />
                  <label style={{ fontSize:'11px', color:'#64748b' }}>Thickness:</label>
                  <input type="range" min="1" max="8"
                    value={selectedElement.outlineThickness || 2}
                    onChange={e => handlePropChange('outlineThickness', parseInt(e.target.value))}
                    onMouseUp={e => handlePropSave('outlineThickness', parseInt(e.target.value))}
                    onTouchEnd={e => handlePropSave('outlineThickness', parseInt(e.currentTarget.value))}
                    style={{ width:'60px', accentColor:'#6366f1' }}
                  />
                  <span style={{ fontSize:'11px', color:'#94a3b8' }}>{selectedElement.outlineThickness || 2}px</span>
                </div>
              )}
            </div>

            {/* Font color swatches */}
            <div>
              <label style={{ fontSize:'11px', color:'#94a3b8', display:'block', marginBottom:'7px' }}>🎨 Font Color</label>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(6,1fr)', gap:'6px', marginBottom:'10px' }}>
                {FONT_COLORS.map(c => (
                  <button key={c.hex} onClick={() => handlePropSave('color', c.hex)} title={c.name}
                    style={{ width:'100%', aspectRatio:'1', backgroundColor:c.hex, border:selectedElement.color===c.hex?'3px solid #6366f1':'1.5px solid #475569', borderRadius:'6px', cursor:'pointer', transform:selectedElement.color===c.hex?'scale(1.18)':'scale(1)', transition:'all 0.12s' }}
                  />
                ))}
              </div>
              <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                <label style={{ fontSize:'11px', color:'#64748b' }}>Custom:</label>
                <input type="color" value={selectedElement.color}
                  onChange={e => handlePropChange('color', e.target.value)}
                  onBlur={e => handlePropSave('color', e.target.value)}
                  style={{ width:'44px', height:'30px', cursor:'pointer', border:'1px solid #475569', borderRadius:'4px', backgroundColor:'transparent' }}
                />
                <span style={{ fontSize:'11px', color:'#94a3b8', fontFamily:'monospace' }}>{selectedElement.color}</span>
              </div>
            </div>

          </div>
        </Section>
      )}

      {/* IMAGE CONTROLS */}
      {selectedElement && selectedElement.type === 'image' && (
        <Section title="🖼️ Image Controls">
          <div style={{ display:'flex', flexDirection:'column', gap:'10px' }}>
            <div>
              <label style={{ fontSize:'11px', color:'#94a3b8', display:'block', marginBottom:'4px' }}>Size: {Math.round(selectedElement.width)}px</label>
              <input type="range" min="30" max="220" value={selectedElement.width || 80}
                onChange={e => handlePropChange('width', parseInt(e.target.value))}
                onMouseUp={e => handlePropSave('width', parseInt(e.target.value))}
                onTouchEnd={e => handlePropSave('width', parseInt(e.currentTarget.value))}
                style={{ width:'100%', accentColor:'#6366f1', height:'6px' }}
              />
            </div>
            <div>
              <label style={{ fontSize:'11px', color:'#94a3b8', display:'block', marginBottom:'4px' }}>Opacity: {Math.round((selectedElement.opacity ?? 1) * 100)}%</label>
              <input type="range" min="10" max="100" value={Math.round((selectedElement.opacity ?? 1) * 100)}
                onChange={e => handlePropChange('opacity', parseInt(e.target.value) / 100)}
                onMouseUp={e => handlePropSave('opacity', parseInt(e.target.value) / 100)}
                onTouchEnd={e => handlePropSave('opacity', parseInt(e.currentTarget.value) / 100)}
                style={{ width:'100%', accentColor:'#6366f1', height:'6px' }}
              />
            </div>
          </div>
        </Section>
      )}

      {/* LAYERS */}
      <Section title={`📚 Layers (${currentElements.length})`} defaultOpen={false}>
        {currentElements.length === 0
          ? <span style={{ fontSize:'12px', color:'#64748b', fontStyle:'italic' }}>No elements yet.</span>
          : <div style={{ display:'flex', flexDirection:'column', gap:'5px', maxHeight:'150px', overflowY:'auto' }}>
              {currentElements.map(el => (
                <div key={el.id} onClick={() => setSelectedElementId(el.id)}
                  style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'9px 12px', backgroundColor:selectedElementId===el.id?'#312e81':'#1e293b', borderRadius:'6px', border:selectedElementId===el.id?'1px solid #6366f1':'1px solid transparent', cursor:'pointer' }}>
                  <span style={{ fontSize:'12px', fontWeight:'600', color:'#fff', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', maxWidth:'200px' }}>
                    {el.type === 'image' ? '🖼️ Image/Logo' : `🔤 "${el.content}"`}
                  </span>
                  <button onClick={e => { e.stopPropagation(); handleDeleteElement(el.id); }} style={{ background:'none', border:'none', color:'#ef4444', fontSize:'15px', cursor:'pointer', padding:'0 4px' }}>✕</button>
                </div>
              ))}
            </div>
        }
      </Section>

      {/* GARMENT COLOR */}
      <Section title="👕 Garment Color">
        <div style={{ display:'flex', gap:'8px', flexWrap:'wrap' }}>
          {SHIRT_SWATCHES.map(sw => (
            <button key={sw.hex} onClick={() => setShirtColor(sw.hex)} title={sw.name}
              style={{ width:'34px', height:'34px', backgroundColor:sw.hex, border:shirtColor===sw.hex?'3px solid #6366f1':'2px solid #475569', borderRadius:'7px', cursor:'pointer', transform:shirtColor===sw.hex?'scale(1.12)':'scale(1)', transition:'all 0.15s' }}
            />
          ))}
        </div>
      </Section>
    </div>
  );

  // ── Order Panel ───────────────────────────────────────────────────────────
  const OrderPanel = () => (
    <div style={{ display:'flex', flexDirection:'column', gap:'16px' }}>

      <div style={{ background:totalQuantity>=12?'linear-gradient(90deg,#15803d,#166534)':'linear-gradient(90deg,#1e293b,#334155)', padding:'12px 16px', borderRadius:'10px', border:'1px solid #475569' }}>
        <span style={{ fontSize:'13px', fontWeight:'600', color:'#f8fafc' }}>{getBulkMsg()}</span>
        {discountPct > 0 && <span style={{ display:'block', marginTop:'4px', backgroundColor:'#fef08a', color:'#854d0e', padding:'2px 8px', borderRadius:'6px', fontSize:'12px', fontWeight:'bold', width:'fit-content' }}>🎉 {discountPct}% OFF Active</span>}
      </div>

      <Section title="👕 Apparel Type">
        <div style={{ display:'flex', flexDirection:'column', gap:'6px' }}>
          {Object.entries(PRODUCTS).map(([key, val]) => (
            <button key={key} onClick={() => setActiveProduct(key)}
              style={{ padding:'11px 14px', fontSize:'14px', fontWeight:'bold', backgroundColor:activeProduct===key?'#6366f1':'#334155', color:'#fff', border:'none', borderRadius:'8px', cursor:'pointer', textAlign:'left' }}>
              {val.name} — <span style={{ color:'#c7d2fe' }}>${val.basePrice}</span>
            </button>
          ))}
        </div>
      </Section>

      <Section title="📦 Quantities">
        {activeProduct === 'tshirt' && (
          <div style={{ display:'flex', gap:'6px', marginBottom:'10px', backgroundColor:'#0f172a', padding:'4px', borderRadius:'10px', border:'1px solid #334155' }}>
            {[{v:'adult',l:'Adult',c:adultCount},{v:'kids',l:'Kids / Youth',c:kidsCount}].map(g => (
              <button key={g.v} onClick={() => setAgeGroup(g.v)}
                style={{ flex:1, padding:'9px 6px', fontSize:'13px', fontWeight:'bold', borderRadius:'8px', border:'none', cursor:'pointer',
                  backgroundColor:ageGroup===g.v?'#6366f1':'transparent', color:ageGroup===g.v?'#fff':'#94a3b8', transition:'all 0.15s' }}>
                {g.l}{g.c>0 ? ` (${g.c})` : ''}
              </button>
            ))}
          </div>
        )}
        {ageGroup === 'kids' && (
          <p style={{ margin:'0 0 8px', fontSize:'11px', color:'#93c5fd' }}>👶 Youth sizes — same price as adult. 2T–4T are toddler; Youth XS–XL fit older kids.</p>
        )}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'8px' }}>
          {activeSizeList.map(size => (
            <div key={size} style={{ textAlign:'center', backgroundColor:'#1e293b', padding:'8px 4px', borderRadius:'8px', border:'1px solid #334155' }}>
              <span style={{ display:'block', fontWeight:'800', color:'#94a3b8', fontSize:size.length>3?'10px':'12px' }}>{size}</span>
              <span style={{ fontSize:'10px', color:'#818cf8', display:'block', margin:'2px 0' }}>${getUnitPrice(size).toFixed(2)}</span>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:'0', marginTop:'4px' }}>
                <button onClick={() => handleSizeChange(size, Math.max(0,(sizes[size]||0)-1))} style={{ width:'26px', height:'30px', backgroundColor:'#0f172a', color:'#94a3b8', border:'1px solid #475569', borderRadius:'6px 0 0 6px', fontSize:'15px', fontWeight:'bold', cursor:'pointer' }}>−</button>
                <div style={{ minWidth:'30px', height:'30px', backgroundColor:'#0f172a', borderTop:'1px solid #475569', borderBottom:'1px solid #475569', display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontWeight:'bold', fontSize:'15px' }}>{sizes[size]||0}</div>
                <button onClick={() => handleSizeChange(size, (sizes[size]||0)+1)} style={{ width:'26px', height:'30px', backgroundColor:'#6366f1', color:'#fff', border:'1px solid #6366f1', borderRadius:'0 6px 6px 0', fontSize:'15px', fontWeight:'bold', cursor:'pointer' }}>+</button>
              </div>
            </div>
          ))}
        </div>
      </Section>

      <Section title="🚚 Fulfillment">
        <div style={{ display:'flex', alignItems:'center', gap:'8px', backgroundColor:'#0c2a4d', border:'1px solid #1e3a8a', borderRadius:'8px', padding:'10px 12px', marginBottom:'12px' }}>
          <span style={{ fontSize:'16px' }}>⏱️</span>
          <span style={{ fontSize:'12px', color:'#93c5fd', fontWeight:'bold' }}>Production time: <span style={{ color:'#fff' }}>2–3 business days</span> after design approval</span>
        </div>
        <div style={{ display:'flex', flexDirection:'column', gap:'10px' }}>
          {[
            { val:'standard',      label:'📦 Standard Tracked — ready in 2–3 business days (Free over $150, else $7.99)', color:'#cbd5e1' },
            { val:'pickup',        label:'🏪 Store Pickup — ready in 2–3 business days, FREE',                         color:'#4ade80' }
          ].map(opt => (
            <label key={opt.val} style={{ display:'flex', alignItems:'center', gap:'10px', cursor:'pointer', color:opt.color, fontWeight:'bold', fontSize:'14px' }}>
              <input type="radio" name="logistics" checked={shippingMethod===opt.val} onChange={() => setShippingMethod(opt.val)} style={{ accentColor:'#6366f1', width:'18px', height:'18px', flexShrink:0 }}/>
              {opt.label}
            </label>
          ))}
        </div>
      </Section>

      <div style={{ fontSize:'13px', color:'#cbd5e1', display:'flex', flexDirection:'column', gap:'8px', backgroundColor:'#0f172a', padding:'16px', borderRadius:'10px', border:'1px solid #334155' }}>
        <div style={{ display:'flex', justifyContent:'space-between' }}><span>Total Units:</span><strong style={{ color:'#fff' }}>{totalQuantity} pcs</strong></div>
        {discountPct > 0 && <div style={{ display:'flex', justifyContent:'space-between', color:'#4ade80' }}><span>Discount:</span><strong>-{discountPct}%</strong></div>}
        <div style={{ display:'flex', justifyContent:'space-between' }}><span>Shipping:</span><strong style={{ color:'#fff' }}>{shippingCost === 0 ? 'FREE' : `$${shippingCost.toFixed(2)}`}</strong></div>
        <div style={{ display:'flex', justifyContent:'space-between' }}><span>Subtotal:</span><strong style={{ color:'#fff' }}>${(merchCents/100).toFixed(2)}</strong></div>
        <div style={{ display:'flex', justifyContent:'space-between' }}><span>Sales Tax (6%):</span><strong style={{ color:'#fff' }}>${salesTax.toFixed(2)}</strong></div>
        <div style={{ display:'flex', justifyContent:'space-between', borderTop:'1px solid #334155', paddingTop:'8px', fontSize:'18px', fontWeight:'800', color:'#fff' }}>
          <span>Total:</span><span style={{ color:'#fef08a' }}>${grandTotal.toFixed(2)}</span>
        </div>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <span style={{ fontSize:'12px', color:'#64748b' }}>⏱️ Est. ready:</span>
          <span style={{ fontSize:'12px', color:'#93c5fd', fontWeight:'bold' }}>2–3 business days</span>
        </div>
      </div>

      {/* Print method */}
      <Section title="🖨️ Print Method">
        <div style={{ display:'flex', flexDirection:'column', gap:'8px' }}>
          {PRINT_METHODS.map(m => (
            <label key={m.val} style={{ display:'flex', alignItems:'flex-start', gap:'10px', cursor:'pointer', padding:'10px', backgroundColor:printMethod===m.val?'#1e3a8a':'#0f172a', borderRadius:'8px', border:`2px solid ${printMethod===m.val?'#3b82f6':'#334155'}`, transition:'all 0.15s' }}>
              <input type="radio" name="printmethod2" checked={printMethod===m.val} onChange={() => setPrintMethod(m.val)} style={{ accentColor:'#6366f1', marginTop:'2px', flexShrink:0 }}/>
              <div style={{ flex:1 }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                  <span style={{ color:'#fff', fontWeight:'bold', fontSize:'13px' }}>{m.icon} {m.label}</span>
                  <span style={{ color:m.surcharge===0?'#4ade80':m.surcharge<=2.5?'#fbbf24':'#fb923c', fontWeight:'800', fontSize:'11px' }}>{m.note}</span>
                </div>
                <span style={{ display:'block', color:'#64748b', fontSize:'11px', marginTop:'2px' }}>{m.desc}</span>
              </div>
            </label>
          ))}
        </div>
      </Section>

      {/* Design notes */}
      <div style={{ backgroundColor:'#0f172a', padding:'14px', borderRadius:'10px', border:'1px solid #334155' }}>
        <label style={{ display:'block', fontSize:'11px', color:'#818cf8', fontWeight:'800', textTransform:'uppercase', letterSpacing:'0.5px', marginBottom:'8px' }}>📝 Notes to Production Team</label>
        <textarea
          value={designNotes}
          onChange={e => setDesignNotes(e.target.value)}
          placeholder="e.g. Please remove white background from logo. Use metallic gold ink. Left chest placement should be 3 inches wide..."
          rows={3}
          style={{ width:'100%', padding:'10px', borderRadius:'8px', border:'1px solid #334155', backgroundColor:'#1e293b', color:'#fff', fontSize:'13px', resize:'vertical', boxSizing:'border-box', fontFamily:'system-ui,sans-serif' }}
        />
        <p style={{ margin:'5px 0 0', fontSize:'10px', color:'#475569' }}>These notes go directly to our production team with your order.</p>
      </div>

      {/* Design location breakdown */}
      {locationFeePerShirt > 0 && (
        <div style={{ backgroundColor:'#0f172a', border:'1px solid #334155', padding:'14px', borderRadius:'10px' }}>
          <span style={{ fontWeight:'800', fontSize:'11px', color:'#818cf8', display:'block', marginBottom:'10px', textTransform:'uppercase', letterSpacing:'0.5px' }}>📍 Design Location Fees (per shirt)</span>
          {Object.keys(LOCATION_FEES).filter(l => locationElements[l]?.length > 0).map(l => {
            const hasImg = locationElements[l].some(e => e.type === 'image');
            const fee = LOCATION_FEES[l] + (hasImg ? COMPLEXITY_FEE : 0);
            const label = l === 'front' ? 'Front' : l === 'back' ? 'Back' : l === 'left_sleeve' ? 'Left Sleeve' : 'Right Sleeve';
            const type = hasImg ? 'Image / Text+Image' : 'Text only';
            return (
              <div key={l} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'5px 0', borderBottom:'1px solid #1e293b' }}>
                <div>
                  <span style={{ color:'#fff', fontWeight:'600', fontSize:'13px' }}>{label}</span>
                  <span style={{ color:'#475569', fontSize:'11px', marginLeft:'8px' }}>{type}</span>
                </div>
                <span style={{ color: fee === 0 ? '#4ade80' : '#fbbf24', fontWeight:'700', fontSize:'13px' }}>
                  {fee === 0 ? 'Included' : `+$${fee.toFixed(2)}/shirt`}
                </span>
              </div>
            );
          })}
          <div style={{ display:'flex', justifyContent:'space-between', paddingTop:'8px', fontSize:'12px', color:'#94a3b8' }}>
            <span>Total location fees:</span>
            <span style={{ color:'#fbbf24', fontWeight:'700' }}>+${locationFeePerShirt.toFixed(2)} per shirt</span>
          </div>
        </div>
      )}

      <div style={{ backgroundColor:'#450a0a', border:'1px solid #991b1b', padding:'13px', borderRadius:'10px' }}>
        <span style={{ fontWeight:'bold', fontSize:'13px', color:'#fca5a5', display:'block', marginBottom:'4px' }}>🚨 ALL SALES FINAL</span>
        <p style={{ margin:0, fontSize:'12px', lineHeight:'1.5', color:'#fecaca' }}>All items custom-printed. No Returns & No Exchanges.</p>
      </div>

      <form onSubmit={handleProcessOrderSubmit} style={{ display:'flex', flexDirection:'column', gap:'12px' }}>
        <div>
          <label style={{ display:'block', fontSize:'11px', color:'#94a3b8', marginBottom:'4px', textTransform:'uppercase' }}>Email Address</label>
          <input type="email" required value={customerEmail} onChange={e => setCustomerEmail(e.target.value)} placeholder="client@domain.com"
            style={{ width:'100%', padding:'13px', borderRadius:'8px', border:'1px solid #475569', backgroundColor:'#0f172a', color:'#fff', fontSize:'16px', boxSizing:'border-box' }}
          />
        </div>
        <div>
          <label style={{ display:'block', fontSize:'11px', color:'#94a3b8', marginBottom:'4px', textTransform:'uppercase' }}>Phone (SMS Updates)</label>
          <input type="tel" value={customerPhone} onChange={e => setCustomerPhone(e.target.value)} placeholder="(757) 000-0000"
            style={{ width:'100%', padding:'13px', borderRadius:'8px', border:'1px solid #475569', backgroundColor:'#0f172a', color:'#fff', fontSize:'16px', boxSizing:'border-box' }}
          />
        </div>
        <div style={{ border:'1px solid #475569', backgroundColor:'#0f172a', padding:'13px', borderRadius:'8px' }}>
          <label style={{ display:'flex', alignItems:'flex-start', gap:'10px', fontSize:'13px', color:'#e2e8f0', cursor:'pointer', fontWeight:'600', lineHeight:'1.5' }}>
            <input type="checkbox" required checked={layoutChecked} onChange={e => setLayoutChecked(e.target.checked)} style={{ marginTop:'2px', accentColor:'#6366f1', width:'18px', height:'18px', flexShrink:0 }}/>
            <span>🔍 I've double-checked spellings, layout, and sizing — and acknowledge all sales are final.</span>
          </label>
        </div>
        <button type="submit" disabled={!totalQuantity || !layoutChecked || isSubmitting}
          style={{ width:'100%', padding:'16px', backgroundColor:(!totalQuantity||!layoutChecked)?'#475569':'#635BFF', color:'white', border:'none', borderRadius:'12px', fontWeight:'800', cursor:(!totalQuantity||!layoutChecked)?'not-allowed':'pointer', fontSize:'16px', textTransform:'uppercase', letterSpacing:'0.5px', boxShadow:(!totalQuantity||!layoutChecked)?'none':'0 4px 20px rgba(99,91,255,0.45)', display:'flex', alignItems:'center', justifyContent:'center', gap:'8px' }}>
          <span style={{ fontSize:'18px' }}>⚡</span>
          {isSubmitting ? 'Processing...' : `Pay with Stripe · $${grandTotal.toFixed(2)}`}
        </button>
        <p style={{ margin:0, fontSize:'11px', color:'#64748b', textAlign:'center' }}>🔒 Secured by Stripe · 256-bit SSL</p>
      </form>
    </div>
  );

  // ── Canvas (stable — not defined inside render) ───────────────────────────
  const renderCanvas = () => (
    <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', backgroundColor:'#334155', borderRadius:'16px', padding:isMobile?'20px 14px':'30px', position:'relative', border:'1px solid #475569', boxShadow:'inset 0 4px 20px rgba(0,0,0,0.4)' }}>
      <div style={{ position:'absolute', top:'10px', left:'50%', transform:'translateX(-50%)', background:'#0f172a', color:'#fff', padding:'4px 12px', borderRadius:'20px', fontSize:'10px', fontWeight:'bold', textTransform:'uppercase', border:'1px solid #475569', whiteSpace:'nowrap', zIndex:5 }}>
        {activeLocation.replace('_', ' ')}
      </div>

      {/* Zoom controls */}
      <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'8px' }}>
        <button onClick={() => setZoomLevel(z => Math.max(0.5, parseFloat((z - 0.25).toFixed(2))))} style={{ padding:'6px 12px', backgroundColor:'#334155', color:'#fff', border:'none', borderRadius:'6px', fontWeight:'bold', fontSize:'14px', cursor:'pointer' }}>−</button>
        <span style={{ color:'#94a3b8', fontSize:'12px', fontWeight:'bold', minWidth:'44px', textAlign:'center' }}>{Math.round(zoomLevel * 100)}%</span>
        <button onClick={() => setZoomLevel(z => Math.min(2, parseFloat((z + 0.25).toFixed(2))))} style={{ padding:'6px 12px', backgroundColor:'#334155', color:'#fff', border:'none', borderRadius:'6px', fontWeight:'bold', fontSize:'14px', cursor:'pointer' }}>+</button>
        <button onClick={() => setZoomLevel(1)} style={{ padding:'5px 10px', backgroundColor:'#1e293b', color:'#64748b', border:'1px solid #334155', borderRadius:'6px', fontSize:'11px', cursor:'pointer' }}>Fit</button>
      </div>

      {/* Shirt silhouette */}
      <div
        style={{ position:'relative', width:`${canvasWidth}px`, height:`${canvasHeight}px`, backgroundColor:shirtColor, border:shirtColor.toLowerCase()==='#ffffff'?'1px solid #cbd5e1':'none', borderRadius:isSleeveActive?'24px':'24px 24px 60px 60px', boxShadow:'0 25px 50px -12px rgba(0,0,0,0.5)', transition:'background-color 0.2s, transform 0.15s', marginTop:'24px', transform:`scale(${zoomLevel})`, transformOrigin:'top center' }}
        onClick={() => { setSelectedElementId(null); setEditingElementId(null); }}
      >
        {!isSleeveActive && (
          <>
            <div style={{ position:'absolute', top:0, left:'50%', transform:'translateX(-50%)', width:isMobile?'85px':'110px', height:isMobile?'22px':'26px', backgroundColor:'#000', opacity:0.12, borderRadius:'0 0 40px 40px' }}/>
            <div style={{ position:'absolute', top:0, left:isMobile?'-34px':'-48px', width:isMobile?'40px':'55px', height:(activeProduct==='tshirt'||activeProduct==='polo')?(isMobile?'85px':'115px'):(isMobile?'210px':'290px'), backgroundColor:shirtColor, borderRadius:'22px 0 0 38px', transform:(activeProduct==='tshirt'||activeProduct==='polo')?'rotate(-18deg)':'rotate(-6deg)', transformOrigin:'top right', zIndex:-1 }}/>
            <div style={{ position:'absolute', top:0, right:isMobile?'-34px':'-48px', width:isMobile?'40px':'55px', height:(activeProduct==='tshirt'||activeProduct==='polo')?(isMobile?'85px':'115px'):(isMobile?'210px':'290px'), backgroundColor:shirtColor, borderRadius:'0 22px 38px 0', transform:(activeProduct==='tshirt'||activeProduct==='polo')?'rotate(18deg)':'rotate(6deg)', transformOrigin:'top left', zIndex:-1 }}/>
            {activeProduct === 'polo' && (
              <>
                <div style={{ position:'absolute', top:0, left:'50%', transform:'translateX(-50%)', width:isMobile?'96px':'124px', height:isMobile?'26px':'32px', backgroundColor:shirtColor, borderRadius:'0 0 12px 12px', borderBottom:'3px solid rgba(0,0,0,0.25)', borderLeft:'2px solid rgba(0,0,0,0.12)', borderRight:'2px solid rgba(0,0,0,0.12)', zIndex:1 }}/>
                <div style={{ position:'absolute', top:isMobile?'26px':'32px', left:'50%', transform:'translateX(-50%)', width:'3px', height:isMobile?'44px':'56px', backgroundColor:'rgba(0,0,0,0.22)', zIndex:1 }}/>
              </>
            )}
            {activeProduct === 'hoodie' && <div style={{ position:'absolute', top:isMobile?'-17px':'-22px', left:'50%', transform:'translateX(-50%)', width:isMobile?'82px':'115px', height:isMobile?'46px':'62px', backgroundColor:shirtColor, borderRadius:'50px 50px 0 0', borderBottom:'5px solid rgba(0,0,0,0.15)', zIndex:1 }}/>}
          </>
        )}

        {/* Print area — overflow:visible so handles aren't clipped */}
        <div
          ref={el => { canvasRef.current = el; printAreaRef.current = el; }}
          onClick={e => e.stopPropagation()}
          style={{
            position:'absolute', top:`${printAreaTop}px`, left:'50%', transform:'translateX(-50%)',
            width:`${printAreaWidth}px`, height:`${printAreaHeight}px`,
            border:'2px dashed rgba(99,102,241,0.5)', borderRadius:'8px',
            overflow:'visible',     // ← FIXED: was hidden, now visible so handles show
            touchAction:'none'      // ← FIXED: prevents browser scroll hijacking during drag
          }}
        >
          {currentElements.map(el => {
            const isSel  = selectedElementId === el.id;
            const isEdit = editingElementId  === el.id;

            // Build text style
            const outlineColor = el.outlineColor || '#000000';
            const txtStyle = {
              color:         el.color,
              fontSize:      `${el.fontSize}px`,
              fontFamily:    el.fontFamily,
              fontWeight:    el.fontWeight || '700',
              letterSpacing: `${el.letterSpacing || 0}px`,
              textShadow: [
                el.hasOutline ? buildOutline(outlineColor, el.outlineThickness || 2) : '',
                el.hasShadow ? `3px 3px 6px ${el.shadowColor || '#000000'}` : ''
              ].filter(Boolean).join(', ') || 'none',
              display:       'block',
              userSelect:    'none',
              WebkitUserSelect: 'none',
              transform: [
                el.rotation ? `rotate(${el.rotation}deg)` : '',
                el.flipH ? 'scaleX(-1)' : '',
                el.flipV ? 'scaleY(-1)' : ''
              ].filter(Boolean).join(' ') || 'none',
              transformOrigin: 'center center'
            };

            if (el.type === 'image') {
              return (
                <div key={el.id}
                  onMouseDown={e => onPointerDown(e, el, 'drag')}
                  onTouchStart={e => onPointerDown(e, el, 'drag')}
                  onClick={e => { e.stopPropagation(); setSelectedElementId(el.id); }}
                  style={{ position:'absolute', left:`${el.x}%`, top:`${el.y}%`, cursor:el.isLocked?'not-allowed':'move', border:isSel?'1.5px dashed #6366f1':'1px solid transparent', padding:'2px', display:'inline-block', zIndex:isSel?10:1, opacity:el.opacity??1, touchAction:'none', transform:[el.flipH?'scaleX(-1)':'',el.flipV?'scaleY(-1)':''].filter(Boolean).join(' ')||'none' }}
                >
                  <img src={el.src} alt="logo" style={{ width:`${el.width||80}px`, height:`${el.width||80}px`, objectFit:'contain', display:'block', pointerEvents:'none', userSelect:'none' }}/>
                  {isSel && !el.isLocked && (
                    <>
                      {/* Delete */}
                      <div
                        onMouseDown={e => { e.stopPropagation(); handleDeleteElement(el.id); }}
                        onTouchEnd={e => { e.stopPropagation(); e.preventDefault(); handleDeleteElement(el.id); }}
                        style={{ position:'absolute', top:'-12px', right:'-12px', width:`${HS}px`, height:`${HS}px`, backgroundColor:'#ef4444', color:'white', borderRadius:'50%', fontSize:'11px', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', fontWeight:'bold', zIndex:20 }}
                      >✕</div>
                      {/* Resize handles */}
                      {Object.entries(cornerHandles).map(([h, s]) => (
                        <div key={h}
                          onMouseDown={e => onPointerDown(e, el, 'resize', h)}
                          onTouchStart={e => onPointerDown(e, el, 'resize', h)}
                          style={{ position:'absolute', width:`${HS}px`, height:`${HS}px`, backgroundColor:'#22c55e', border:'2px solid white', borderRadius:'50%', zIndex:20, touchAction:'none', ...s }}
                        />
                      ))}
                    </>
                  )}
                </div>
              );
            }

            // TEXT element
            return (
              <div key={el.id}
                onMouseDown={e => { if (!isEdit) onPointerDown(e, el, 'drag'); }}
                onTouchStart={e => { if (!isEdit) onPointerDown(e, el, 'drag'); }}
                onClick={e => { e.stopPropagation(); setSelectedElementId(el.id); }}
                onDoubleClick={e => { e.stopPropagation(); startEditingElement(el); }}
                style={{ position:'absolute', left:`${el.x}%`, top:`${el.y}%`, cursor:el.isLocked?'not-allowed':(isEdit?'text':'move'), border:isSel?'1.5px dashed #6366f1':'1px solid transparent', padding:'4px', display:'inline-block', whiteSpace:'nowrap', zIndex:isSel?10:1, touchAction:isEdit?'auto':'none' }}
              >
                {isEdit ? (
                  <input
                    autoFocus
                    value={editingText}
                    onChange={e => setEditingText(e.target.value)}
                    onBlur={commitEdit}
                    onKeyDown={e => { if (e.key==='Enter') { e.preventDefault(); commitEdit(); } if (e.key==='Escape') { setEditingElementId(null); setEditingText(''); } }}
                    onClick={e => e.stopPropagation()}
                    onMouseDown={e => e.stopPropagation()}
                    onTouchStart={e => e.stopPropagation()}
                    style={{ ...txtStyle, background:'rgba(99,102,241,0.15)', border:'1px solid #6366f1', borderRadius:'3px', outline:'none', padding:'2px 4px', minWidth:'60px', width:`${Math.max(editingText.length, 4) * (el.fontSize * 0.6)}px`, caretColor:'#6366f1' }}
                  />
                ) : (
                  <span style={txtStyle}>{el.content}</span>
                )}

                {isSel && !el.isLocked && !isEdit && (
                  <>
                    {/* Delete */}
                    <div
                      onMouseDown={e => { e.stopPropagation(); handleDeleteElement(el.id); }}
                      onTouchEnd={e => { e.stopPropagation(); e.preventDefault(); handleDeleteElement(el.id); }}
                      style={{ position:'absolute', top:'-12px', right:'-12px', width:`${HS}px`, height:`${HS}px`, backgroundColor:'#ef4444', color:'white', borderRadius:'50%', fontSize:'11px', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', fontWeight:'bold', zIndex:20 }}
                    >✕</div>
                    {/* Edit pencil */}
                    <div
                      onMouseDown={e => { e.stopPropagation(); startEditingElement(el); }}
                      onTouchEnd={e => { e.stopPropagation(); e.preventDefault(); startEditingElement(el); }}
                      style={{ position:'absolute', top:'-12px', left:'-12px', width:`${HS}px`, height:`${HS}px`, backgroundColor:'#f59e0b', color:'white', borderRadius:'50%', fontSize:'11px', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', fontWeight:'bold', zIndex:20 }}
                    >✎</div>
                    {/* Resize handles */}
                    {Object.entries(cornerHandles).map(([h, s]) => (
                      <div key={h}
                        onMouseDown={e => onPointerDown(e, el, 'resize', h)}
                        onTouchStart={e => onPointerDown(e, el, 'resize', h)}
                        style={{ position:'absolute', width:`${HS}px`, height:`${HS}px`, backgroundColor:'#6366f1', border:'2px solid white', borderRadius:'50%', zIndex:20, touchAction:'none', ...s }}
                      />
                    ))}
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Mobile quick-action bar */}
      {isMobile && (
        <div style={{ display:'flex', gap:'8px', marginTop:'16px', width:'100%' }}>
          <button onClick={undo} disabled={!canUndo} style={{ flex:1, padding:'12px', backgroundColor:canUndo?'#1e3a8a':'#1e293b', color:canUndo?'#93c5fd':'#475569', border:`1px solid ${canUndo?'#3b82f6':'#334155'}`, borderRadius:'8px', fontWeight:'800', fontSize:'14px', cursor:canUndo?'pointer':'not-allowed' }}>↩</button>
          <button onClick={redo} disabled={!canRedo} style={{ flex:1, padding:'12px', backgroundColor:canRedo?'#1e3a8a':'#1e293b', color:canRedo?'#93c5fd':'#475569', border:`1px solid ${canRedo?'#3b82f6':'#334155'}`, borderRadius:'8px', fontWeight:'800', fontSize:'14px', cursor:canRedo?'pointer':'not-allowed' }}>↪</button>
          <button onClick={() => { const el = document.getElementById('mobile-design-tools'); if(el) el.scrollIntoView({behavior:'smooth'}); }} style={{ flex:2, padding:'12px', backgroundColor:'#6366f1', color:'#fff', border:'none', borderRadius:'8px', fontWeight:'800', fontSize:'14px', cursor:'pointer' }}>✏️ Edit Design</button>
        </div>
      )}
    </div>
  );

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div
      style={{ display:'flex', flexDirection:'column', gap:'16px', padding:isMobile?'12px':'24px', fontFamily:'system-ui,sans-serif', backgroundColor:'#0f172a', color:'#f8fafc', minHeight:'100vh', width:'100%', boxSizing:'border-box' }}
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}
    >
      {showStripe     && <StripeModal grandTotal={grandTotal} orderData={orderData} getDesigns={() => buildDesigns(locationElements, shirtColor)} onClose={() => setShowStripe(false)} onSuccess={handlePaymentSuccess}/>}
      {paymentSuccess && <PaymentSuccessModal onClose={() => setPaymentSuccess(false)}/>}

      {/* HEADER — purple gradient card with name, tagline, phone & email */}
      <div style={{ background:'linear-gradient(135deg,#1e1b4b,#311042)', padding:isMobile?'16px':'24px 32px', borderRadius:'16px', border:'1px solid #3b0764', display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:'12px' }}>
        <div>
          <div style={{ display:'flex', alignItems:'center', gap:'8px', flexWrap:'wrap' }}>
            <h1 style={{ margin:0, fontSize:isMobile?'22px':'30px', color:'#fff', fontWeight:'800' }}>✨ Customized Teez</h1>
            <span style={{ backgroundColor:'#de1c5d', color:'#fff', fontSize:'9px', padding:'2px 7px', borderRadius:'20px', fontWeight:'bold', textTransform:'uppercase' }}>Studio Pro</span>
          </div>
          <p style={{ margin:'4px 0 0', fontSize:'13px', color:'#c7d2fe', fontStyle:'italic', fontWeight:'bold' }}>"You Name it We Make it"</p>
          <div style={{ marginTop:'8px', fontSize:'12px', color:'#93c5fd', display:'flex', gap:'16px', flexWrap:'wrap' }}>
            <a href="tel:7576796995" style={{ color:'#fff', fontWeight:'bold', textDecoration:'none' }}>📞 (757) 679-6995</a>
            <a href="mailto:customizedteez@gmail.com" style={{ color:'#e0e7ff', textDecoration:'none' }}>✉️ customizedteez@gmail.com</a>
            {!isMobile && <span style={{ color:'#93c5fd' }}>📍 926 Monarch Dr, Virginia Beach VA</span>}
          </div>
        </div>
        <div style={{ display:'flex', gap:'8px', alignItems:'center' }}>
          <button onClick={() => setShowAdmin(a => !a)} style={{ padding:'11px 15px', backgroundColor:'#312e81', color:'#c7d2fe', border:'1px solid #4338ca', borderRadius:'10px', fontWeight:'bold', fontSize:'13px', cursor:'pointer' }}>
            🎛️ Admin
          </button>
        </div>
      </div>

      {showAdmin && <AdminPanel/>}

      {/* DESKTOP LAYOUT */}
      {!isMobile && (
        <>
          <div style={{ backgroundColor:'#1e293b', padding:'14px 18px', borderRadius:'12px', display:'flex', gap:'8px', alignItems:'center', flexWrap:'wrap', border:'1px solid #334155' }}>
            <span style={{ fontWeight:'bold', color:'#cbd5e1', fontSize:'13px' }}>👕 Apparel:</span>
            {Object.entries(PRODUCTS).map(([key, val]) => (
              <button key={key} onClick={() => { setActiveProduct(key); setSelectedElementId(null); }}
                style={{ padding:'8px 14px', fontSize:'12px', fontWeight:'bold', backgroundColor:activeProduct===key?'#6366f1':'#334155', color:'#fff', border:'none', borderRadius:'8px', cursor:'pointer', boxShadow:activeProduct===key?'0 0 12px rgba(99,102,241,0.4)':'none' }}>
                {val.name}
              </button>
            ))}
          </div>
          <div style={{ display:'flex', gap:'20px', width:'100%', alignItems:'flex-start' }}>
            <div style={{ width:'380px', minWidth:'380px', padding:'16px', backgroundColor:'#1e293b', borderRadius:'14px', border:'1px solid #334155', boxSizing:'border-box', maxHeight:'85vh', overflowY:'auto' }}>
              {DesignPanel()}
            </div>
            <div style={{ flexGrow:1 }}>{renderCanvas()}</div>
          </div>
          <div style={{ backgroundColor:'#1e293b', padding:'24px', borderRadius:'16px', border:'1px solid #334155' }}>
            {OrderPanel()}
          </div>
        </>
      )}

      {/* MOBILE LAYOUT — canvas always visible, tools scroll below */}
      {isMobile && (
        <>
          {/* ── Shirt preview — always on screen ── */}
          {renderCanvas()}

          {/* ── Tool panels — scroll below the canvas ── */}
          <div id="mobile-design-tools" style={{ display:'flex', flexDirection:'column', gap:'12px' }}>

            {/* Quick add-text bar — always visible so customer never has to hunt */}
            <div style={{ backgroundColor:'#1e293b', borderRadius:'12px', border:'1px solid #334155', padding:'12px 14px' }}>
              <p style={{ margin:'0 0 8px', fontSize:'11px', color:'#818cf8', fontWeight:'800', textTransform:'uppercase', letterSpacing:'0.5px' }}>✏️ Add Text to Shirt</p>
              <div style={{ display:'flex', gap:'8px' }}>
                <input
                  type="text"
                  value={textInput}
                  onChange={e => setTextInput(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && addText()}
                  placeholder="Type your wording here..."
                  style={{ flexGrow:1, padding:'12px', borderRadius:'8px', border:'1px solid #475569', backgroundColor:'#0f172a', color:'#fff', fontSize:'15px' }}
                />
                <button onClick={addText} style={{ padding:'12px 18px', backgroundColor:'#6366f1', color:'white', border:'none', borderRadius:'8px', fontWeight:'bold', fontSize:'15px', cursor:'pointer' }}>Add</button>
              </div>
            </div>

            {/* Upload image bar — always visible */}
            <div style={{ backgroundColor:'#1e293b', borderRadius:'12px', border:'1px solid #334155', padding:'12px 14px' }}>
              <input ref={imageInputRef} type="file" accept="image/*" onChange={handleImageUpload} style={{ display:'none' }}/>
              <button onClick={() => imageInputRef.current.click()} style={{ width:'100%', padding:'12px', backgroundColor:'#312e81', color:'#c7d2fe', border:'1px solid #4338ca', borderRadius:'8px', fontWeight:'700', fontSize:'14px', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:'8px' }}>
                🖼️ Upload Logo / Image
              </button>
            </div>

            {/* Placement zone — always visible so they can switch front/back/sleeve */}
            <div style={{ backgroundColor:'#1e293b', borderRadius:'12px', border:'1px solid #334155', padding:'12px 14px' }}>
              <p style={{ margin:'0 0 8px', fontSize:'11px', color:'#818cf8', fontWeight:'800', textTransform:'uppercase', letterSpacing:'0.5px' }}>🎯 Print Location</p>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'6px' }}>
                {[{id:'front',label:'Front'},{id:'back',label:'Back'},{id:'left_sleeve',label:'Left Sleeve'},{id:'right_sleeve',label:'Right Sleeve'}].map(loc => (
                  <button key={loc.id}
                    onClick={() => { setActiveLocation(loc.id); setSelectedElementId(null); setEditingElementId(null); }}
                    style={{ padding:'10px 6px', fontSize:'13px', fontWeight:'bold', backgroundColor:activeLocation===loc.id?'#6366f1':'#334155', color:'#fff', border:'none', borderRadius:'6px', cursor:'pointer' }}>
                    {loc.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Undo / Redo */}
            <div style={{ display:'flex', gap:'8px' }}>
              <button onClick={undo} disabled={!canUndo} style={{ flex:1, padding:'12px', backgroundColor:canUndo?'#1e3a8a':'#1e293b', color:canUndo?'#93c5fd':'#475569', border:`1px solid ${canUndo?'#3b82f6':'#334155'}`, borderRadius:'8px', fontWeight:'800', fontSize:'14px', cursor:canUndo?'pointer':'not-allowed' }}>↩ Undo</button>
              <button onClick={redo} disabled={!canRedo} style={{ flex:1, padding:'12px', backgroundColor:canRedo?'#1e3a8a':'#1e293b', color:canRedo?'#93c5fd':'#475569', border:`1px solid ${canRedo?'#3b82f6':'#334155'}`, borderRadius:'8px', fontWeight:'800', fontSize:'14px', cursor:canRedo?'pointer':'not-allowed' }}>Redo ↪</button>
            </div>

            {/* Selected element controls — only shown when something is tapped */}
            {selectedElementId && (
              <div style={{ backgroundColor:'#1e293b', borderRadius:'12px', border:'1px solid #4338ca', padding:'12px 14px' }}>
                <p style={{ margin:'0 0 10px', fontSize:'11px', color:'#818cf8', fontWeight:'800', textTransform:'uppercase', letterSpacing:'0.5px' }}>
                  {selectedElement?.type === 'image' ? '🖼️ Image Controls' : '🔤 Text Controls'}
                </p>

                {/* Element action buttons */}
                <div style={{ display:'flex', gap:'8px', marginBottom:'12px' }}>
                  <button onClick={centerEl} style={{ flexGrow:1, padding:'10px', backgroundColor:'#312e81', color:'#e0e7ff', border:'1px solid #4338ca', borderRadius:'6px', fontSize:'13px', fontWeight:'bold', cursor:'pointer' }}>🎯 Center</button>
                  <button onClick={() => toggleLock(selectedElementId)} style={{ padding:'10px 14px', backgroundColor:selectedElement?.isLocked?'#9a3412':'#475569', color:'#fff', border:'none', borderRadius:'6px', fontSize:'13px', fontWeight:'bold', cursor:'pointer' }}>
                    {selectedElement?.isLocked ? '🔒' : '🔓'}
                  </button>
                  <button onClick={() => handleDeleteElement(selectedElementId)} style={{ padding:'10px 14px', backgroundColor:'#7f1d1d', color:'#fca5a5', border:'none', borderRadius:'6px', fontSize:'13px', fontWeight:'bold', cursor:'pointer' }}>🗑️</button>
                </div>

                {/* TEXT controls */}
                {selectedElement && selectedElement.type === 'text' && (
                  <div style={{ display:'flex', flexDirection:'column', gap:'12px' }}>

                    {/* Edit text inline */}
                    <div>
                      <label style={{ fontSize:'11px', color:'#94a3b8', display:'block', marginBottom:'5px' }}>Edit Text</label>
                      <div style={{ display:'flex', gap:'6px' }}>
                        <input type="text"
                          value={editingElementId === selectedElement.id ? editingText : selectedElement.content}
                          onChange={e => { setEditingElementId(selectedElement.id); setEditingText(e.target.value); }}
                          onFocus={() => { setEditingElementId(selectedElement.id); setEditingText(selectedElement.content); }}
                          onBlur={commitEdit}
                          onKeyDown={e => { if (e.key==='Enter'){e.preventDefault();commitEdit();} if(e.key==='Escape'){setEditingElementId(null);setEditingText('');} }}
                          style={{ flexGrow:1, padding:'10px', borderRadius:'6px', border:'1px solid #4338ca', backgroundColor:'#0f172a', color:'#fff', fontSize:'15px', outline:'none' }}
                        />
                        <button onMouseDown={e=>{e.preventDefault();commitEdit();}} style={{ padding:'10px 14px', backgroundColor:'#4338ca', color:'white', border:'none', borderRadius:'6px', fontWeight:'bold', fontSize:'13px', cursor:'pointer' }}>Save</button>
                      </div>
                    </div>

                    {/* Font */}
                    <div>
                      <label style={{ fontSize:'11px', color:'#94a3b8', display:'block', marginBottom:'5px' }}>Font Style</label>
                      <select value={selectedElement.fontFamily} onChange={e => handlePropSave('fontFamily', e.target.value)}
                        style={{ width:'100%', padding:'11px', borderRadius:'6px', backgroundColor:'#0f172a', color:'#fff', border:'1px solid #475569', fontSize:'14px' }}>
                        {FONTS.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                      </select>
                    </div>

                    {/* Weight */}
                    <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'6px' }}>
                      {WEIGHTS.map(w => (
                        <button key={w.value} onClick={() => handlePropSave('fontWeight', w.value)}
                          style={{ padding:'10px 4px', fontSize:'13px', fontWeight:w.value, backgroundColor:selectedElement.fontWeight===w.value?'#6366f1':'#334155', color:'#fff', border:'none', borderRadius:'6px', cursor:'pointer' }}>
                          {w.label}
                        </button>
                      ))}
                    </div>

                    {/* Letter spacing */}
                    <div>
                      <label style={{ fontSize:'11px', color:'#94a3b8', display:'block', marginBottom:'4px' }}>Letter Spacing: {selectedElement.letterSpacing || 0}px</label>
                      <input type="range" min="-2" max="15"
                        value={selectedElement.letterSpacing || 0}
                        onChange={e => handlePropChange('letterSpacing', parseInt(e.target.value))}
                        onTouchEnd={e => handlePropSave('letterSpacing', parseInt(e.currentTarget.value))}
                        onMouseUp={e => handlePropSave('letterSpacing', parseInt(e.target.value))}
                        style={{ width:'100%', accentColor:'#6366f1', height:'8px' }}
                      />
                    </div>

                    {/* Rotation */}
                    <div>
                      <label style={{ fontSize:'11px', color:'#94a3b8', display:'block', marginBottom:'4px' }}>↻ Rotation: {selectedElement.rotation || 0}°</label>
                      <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                        <input type="range" min="-180" max="180"
                          value={selectedElement.rotation || 0}
                          onChange={e => handlePropChange('rotation', parseInt(e.target.value))}
                          onMouseUp={e => handlePropSave('rotation', parseInt(e.target.value))}
                          onTouchEnd={e => handlePropSave('rotation', parseInt(e.currentTarget.value))}
                          style={{ flexGrow:1, accentColor:'#6366f1', height:'8px' }}
                        />
                        <button onClick={() => handlePropSave('rotation', 0)} style={{ padding:'6px 10px', backgroundColor:'#334155', color:'#94a3b8', border:'none', borderRadius:'4px', fontSize:'11px', cursor:'pointer' }}>Reset</button>
                      </div>
                    </div>

                    {/* Shadow */}
                    <label style={{ display:'flex', alignItems:'center', gap:'8px', fontSize:'14px', color:'#94a3b8', cursor:'pointer' }}>
                      <input type="checkbox" checked={selectedElement.hasShadow||false} onChange={e=>handlePropSave('hasShadow',e.target.checked)} style={{ accentColor:'#6366f1', width:'20px', height:'20px' }}/>
                      Drop Shadow
                      {selectedElement.hasShadow && (
                        <input type="color" value={selectedElement.shadowColor||'#000000'} onChange={e=>handlePropChange('shadowColor',e.target.value)} onBlur={e=>handlePropSave('shadowColor',e.target.value)} style={{ width:'36px', height:'28px', cursor:'pointer', border:'1px solid #475569', borderRadius:'4px', backgroundColor:'transparent', marginLeft:'4px' }}/>
                      )}
                    </label>

                    {/* Outline */}
                    <div style={{ display:'flex', alignItems:'center', gap:'14px', flexWrap:'wrap' }}>
                      <label style={{ display:'flex', alignItems:'center', gap:'8px', fontSize:'14px', color:'#94a3b8', cursor:'pointer' }}>
                        <input type="checkbox" checked={selectedElement.hasOutline || false} onChange={e => handlePropSave('hasOutline', e.target.checked)} style={{ accentColor:'#6366f1', width:'20px', height:'20px' }}/>
                        Pop Outline
                      </label>
                      {selectedElement.hasOutline && (
                        <div style={{ display:'flex', alignItems:'center', gap:'6px' }}>
                          <label style={{ fontSize:'12px', color:'#64748b' }}>Color:</label>
                          <input type="color" value={selectedElement.outlineColor || '#000000'}
                            onChange={e => handlePropChange('outlineColor', e.target.value)}
                            onBlur={e => handlePropSave('outlineColor', e.target.value)}
                            style={{ width:'44px', height:'32px', cursor:'pointer', border:'1px solid #475569', borderRadius:'4px', backgroundColor:'transparent' }}
                          />
                        </div>
                      )}
                    </div>

                    {/* Font colors */}
                    <div>
                      <label style={{ fontSize:'11px', color:'#94a3b8', display:'block', marginBottom:'8px' }}>🎨 Text Color</label>
                      <div style={{ display:'grid', gridTemplateColumns:'repeat(6,1fr)', gap:'7px', marginBottom:'10px' }}>
                        {FONT_COLORS.map(c => (
                          <button key={c.hex} onClick={() => handlePropSave('color', c.hex)} title={c.name}
                            style={{ width:'100%', aspectRatio:'1', backgroundColor:c.hex, border:selectedElement.color===c.hex?'3px solid #6366f1':'1.5px solid #475569', borderRadius:'6px', cursor:'pointer', transform:selectedElement.color===c.hex?'scale(1.15)':'scale(1)', transition:'all 0.12s' }}
                          />
                        ))}
                      </div>
                      <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                        <label style={{ fontSize:'12px', color:'#64748b' }}>Custom:</label>
                        <input type="color" value={selectedElement.color}
                          onChange={e => handlePropChange('color', e.target.value)}
                          onBlur={e => handlePropSave('color', e.target.value)}
                          style={{ width:'48px', height:'32px', cursor:'pointer', border:'1px solid #475569', borderRadius:'4px', backgroundColor:'transparent' }}
                        />
                        <span style={{ fontSize:'11px', color:'#94a3b8', fontFamily:'monospace' }}>{selectedElement.color}</span>
                      </div>
                    </div>

                  </div>
                )}

                {/* IMAGE controls */}
                {selectedElement && selectedElement.type === 'image' && (
                  <div style={{ display:'flex', flexDirection:'column', gap:'12px' }}>
                    <div>
                      <label style={{ fontSize:'11px', color:'#94a3b8', display:'block', marginBottom:'4px' }}>Size: {Math.round(selectedElement.width)}px</label>
                      <input type="range" min="30" max="220" value={selectedElement.width || 80}
                        onChange={e => handlePropChange('width', parseInt(e.target.value))}
                        onMouseUp={e => handlePropSave('width', parseInt(e.target.value))}
                        onTouchEnd={e => handlePropSave('width', parseInt(e.currentTarget.value))}
                        style={{ width:'100%', accentColor:'#6366f1', height:'8px' }}
                      />
                    </div>
                    <div>
                      <label style={{ fontSize:'11px', color:'#94a3b8', display:'block', marginBottom:'4px' }}>Opacity: {Math.round((selectedElement.opacity ?? 1) * 100)}%</label>
                      <input type="range" min="10" max="100" value={Math.round((selectedElement.opacity ?? 1) * 100)}
                        onChange={e => handlePropChange('opacity', parseInt(e.target.value) / 100)}
                        onMouseUp={e => handlePropSave('opacity', parseInt(e.target.value) / 100)}
                        onTouchEnd={e => handlePropSave('opacity', parseInt(e.currentTarget.value) / 100)}
                        style={{ width:'100%', accentColor:'#6366f1', height:'8px' }}
                      />
                    </div>
                  </div>
                )}

              </div>
            )}

            {/* Layers list */}
            {currentElements.length > 0 && (
              <div style={{ backgroundColor:'#1e293b', borderRadius:'12px', border:'1px solid #334155', padding:'12px 14px' }}>
                <p style={{ margin:'0 0 8px', fontSize:'11px', color:'#818cf8', fontWeight:'800', textTransform:'uppercase', letterSpacing:'0.5px' }}>📚 Layers ({currentElements.length})</p>
                <div style={{ display:'flex', flexDirection:'column', gap:'5px' }}>
                  {currentElements.map(el => (
                    <div key={el.id} onClick={() => setSelectedElementId(el.id)}
                      style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'10px 12px', backgroundColor:selectedElementId===el.id?'#312e81':'#0f172a', borderRadius:'8px', border:selectedElementId===el.id?'1px solid #6366f1':'1px solid #334155', cursor:'pointer' }}>
                      <span style={{ fontSize:'13px', fontWeight:'600', color:'#fff', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', maxWidth:'220px' }}>
                        {el.type === 'image' ? '🖼️ Image/Logo' : `🔤 "${el.content}"`}
                      </span>
                      <button onClick={e => { e.stopPropagation(); handleDeleteElement(el.id); }} style={{ background:'none', border:'none', color:'#ef4444', fontSize:'16px', cursor:'pointer', padding:'0 4px' }}>✕</button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Garment color */}
            <div style={{ backgroundColor:'#1e293b', borderRadius:'12px', border:'1px solid #334155', padding:'12px 14px' }}>
              <p style={{ margin:'0 0 10px', fontSize:'11px', color:'#818cf8', fontWeight:'800', textTransform:'uppercase', letterSpacing:'0.5px' }}>👕 Garment Color</p>
              <div style={{ display:'flex', gap:'8px', flexWrap:'wrap' }}>
                {SHIRT_SWATCHES.map(sw => (
                  <button key={sw.hex} onClick={() => setShirtColor(sw.hex)} title={sw.name}
                    style={{ width:'36px', height:'36px', backgroundColor:sw.hex, border:shirtColor===sw.hex?'3px solid #6366f1':'2px solid #475569', borderRadius:'8px', cursor:'pointer', transform:shirtColor===sw.hex?'scale(1.12)':'scale(1)', transition:'all 0.15s' }}
                  />
                ))}
              </div>
            </div>

            {/* Order section */}
            <div style={{ backgroundColor:'#1e293b', borderRadius:'12px', border:'1px solid #334155', padding:'12px 14px' }}>
              {OrderPanel()}
            </div>

          </div>

          {/* No bottom tab bar needed — everything is on one scrollable page */}
        </>
      )}
    </div>
  );
}
